import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Lock } from "lucide-react";

import {
  DESIGN_PROCESS,
  ROADMAP,
  WORKING_TOGETHER,
  parseSalutation,
} from "./portalContent";
import { STYLE_DEFS, COLOR_DEFS, PREF_DEFS } from "./surveyData";
import Logo from "../components/Logo";
import FolkMoodboard from "../components/FolkMoodboard";
import ImageLightbox from "../components/ImageLightbox";
import { useHomeownerAuth } from "./HomeownerAuthContext";
import {
  ShareLinkBar, LeaveMessageWidget, ReactionRow, FirstVisitTour,
  StyleAtGlanceCard,
} from "../components/PortalWidgets";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SECTION_HUES = {
  yellow: { bg: "#FFFBC9", text: "#68280E", border: "rgba(104, 40, 14, 0.18)", soft: "rgba(255, 251, 201, 0.55)" },
  maroon: { bg: "#68280E", text: "#FFFFFF", border: "rgba(104, 40, 14, 0.35)", soft: "rgba(104, 40, 14, 0.08)" },
  blue:   { bg: "#BADDF0", text: "#0F3A54", border: "rgba(27, 74, 102, 0.25)", soft: "rgba(186, 221, 240, 0.5)" },
};

const SECTION_GROUPS = [
  {
    label: "My project",
    items: [
      { key: "report",    label: "Your report",     hue: "maroon" },
      { key: "welcome",   label: "Welcome",         hue: "yellow" },
      { key: "your_designer", label: "Your designer", hue: "maroon" },
      { key: "status",    label: "Live status",     hue: "blue"   },
      { key: "you",       label: "Getting to know you", hue: "yellow" },
      { key: "home_details", label: "Home details",     hue: "blue"   },
      { key: "billing",   label: "Billing",         hue: "maroon" },
      { key: "refer",     label: "Refer a friend",  hue: "blue"   },
    ],
  },
  {
    label: "Design library",
    items: [
      { key: "project_moodboard", label: "Moodboards",       hue: "blue"   },
      { key: "sketches",          label: "Sketches",         hue: "blue"   },
      { key: "style_refs",        label: "References",       hue: "blue"   },
      { key: "layouts",           label: "Layout library",   hue: "blue"   },
    ],
  },
  {
    label: "Your project",
    items: [
      { key: "renders",           label: "Project renders",       hue: "blue"   },
      { key: "custom_furniture",  label: "Custom furniture",      hue: "maroon" },
      { key: "materials_finishes",label: "Materials & finishes",  hue: "yellow" },
      { key: "paint_textures",    label: "Paint & textures",      hue: "blue"   },
      { key: "upholstery",        label: "Upholstery & furnishings", hue: "maroon" },
      { key: "objects",           label: "Objects",               hue: "blue"   },
    ],
  },
  {
    label: "How we work",
    items: [
      { key: "process",   label: "Design process",  hue: "blue"   },
      { key: "roadmap",   label: "Roadmap",         hue: "blue"   },
      { key: "together",  label: "Working together", hue: "yellow" },
    ],
  },
  {
    label: "Fine print",
    items: [
      { key: "terms",     label: "Terms",           hue: "maroon" },
    ],
  },
];

const SECTIONS = SECTION_GROUPS.flatMap((g) => g.items.map((it) => ({ ...it, status: "live" })));

export const PROGRESS_PHASES = [
  { key: "discovery", label: "Discovery" },
  { key: "concept", label: "Concept" },
  { key: "design_dev", label: "Design Development" },
  { key: "sourcing", label: "Sourcing" },
  { key: "install", label: "Install" },
  { key: "handover", label: "Handover" },
];

export default function Portal() {
  const { id } = useParams();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [content, setContent] = useState(null);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [section, setSection] = useState(location.pathname.endsWith("/welcome") ? "welcome" : "report");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem("folk.portal.sidebarCollapsed") === "1"; } catch { return false; }
  });
  const toggleSidebar = () => {
    setSidebarCollapsed((v) => {
      const next = !v;
      try { localStorage.setItem("folk.portal.sidebarCollapsed", next ? "1" : "0"); } catch { /* noop */ }
      return next;
    });
  };
  const [generatingWelcome, setGeneratingWelcome] = useState(false);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const [portalRes, contentRes, reportRes] = await Promise.all([
          axios.get(`${API}/portal/${id}`, { withCredentials: false }),
          axios.get(`${API}/content`, { withCredentials: false }),
          axios.get(`${API}/portal/${id}/report`, { withCredentials: false }),
        ]);
        if (!mounted) return;
        setData(portalRes.data);
        setContent(contentRes.data);
        setReport(reportRes.data);
      } catch (e) {
        if (!mounted) return;
        setError(e.response?.status === 404 ? "Portal not found" : "Something went wrong");
      }
    };
    load();
    // Poll every 30s for live progress updates
    const iv = setInterval(async () => {
      try {
        const [r, rep] = await Promise.all([
          axios.get(`${API}/portal/${id}`, { withCredentials: false }),
          axios.get(`${API}/portal/${id}/report`, { withCredentials: false }),
        ]);
        if (mounted) { setData(r.data); setReport(rep.data); }
      } catch { /* ignore transient */ }
    }, 30000);
    return () => { mounted = false; clearInterval(iv); };
  }, [id]);

  useEffect(() => {
    if (data && !data.persona_summary && !generatingWelcome) {
      setGeneratingWelcome(true);
      axios
        .post(`${API}/portal/${id}/generate-welcome`, {}, { withCredentials: false })
        .then((r) => setData((d) => ({ ...d, persona_summary: r.data.persona_summary })))
        .catch(() => null)
        .finally(() => setGeneratingWelcome(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id]);

  const salutation = useMemo(() => parseSalutation(data?.contact_name), [data]);

  if (error) return <PortalError message={error} />;
  if (!data || !content) return <PortalLoading />;

  return (
    <div style={{ minHeight: "100vh", background: "var(--folk-bg)", color: "var(--folk-text)" }}>
      <header style={{ maxWidth: 1080, margin: "0 auto", padding: "40px 24px 12px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
        <div>
          <div className="folk-eyebrow" data-testid="portal-eyebrow">
            Homeowner portal
          </div>
          <h1 className="folk-h1" style={{ marginTop: 6 }} data-testid="portal-title">
            Welcome, {salutation.displayName}.
          </h1>
          <div style={{ color: "var(--folk-muted)", fontSize: 15, marginTop: 6 }} data-testid="portal-prepared-for">
            A little guide to what happens next. Everything we'll build on together lives here.
          </div>
        </div>
        <PortalSessionBadge />
      </header>

      {/* Global widgets: floating Ask Folk button + first-visit tour */}
      <LeaveMessageWidget submissionId={id} defaultEmail={data?.contact_email} />
      <FirstVisitTour submissionId={id} />

      <div className={`folk-portal-body${sidebarCollapsed ? " is-sidebar-collapsed" : ""}`}>
      <nav
        className={`folk-sidebar${sidebarCollapsed ? " is-collapsed" : ""}`}
        data-testid="portal-sidebar"
      >
        <button
          type="button"
          data-testid="portal-sidebar-collapse"
          className="folk-sidebar__collapse-btn"
          onClick={toggleSidebar}
          aria-label={sidebarCollapsed ? "Expand navigation" : "Collapse navigation"}
          title={sidebarCollapsed ? "Expand menu" : "Collapse menu"}
        >
          <span aria-hidden style={{ fontSize: 14, lineHeight: 1 }}>{sidebarCollapsed ? "›" : "‹"}</span>
          {!sidebarCollapsed && <span className="folk-sidebar__collapse-label">collapse</span>}
        </button>
        {!sidebarCollapsed && (
          <SidebarNav
            groups={SECTION_GROUPS}
            section={section}
            setSection={(k) => { setSection(k); setDrawerOpen(false); }}
          />
        )}
      </nav>

      {/* Mobile drawer trigger */}
      <button
        type="button"
        className="folk-sidebar-toggle"
        data-testid="portal-menu-toggle"
        onClick={() => setDrawerOpen(true)}
        aria-label="Open navigation"
      >
        <span aria-hidden style={{ fontSize: 20 }}>☰</span>
        <span style={{ fontSize: 14, fontWeight: 500 }}>
          {SECTIONS.find((s) => s.key === section)?.label || "Menu"}
        </span>
      </button>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div
          className="folk-drawer-backdrop"
          data-testid="portal-drawer-backdrop"
          onClick={() => setDrawerOpen(false)}
        >
          <aside
            className="folk-drawer"
            data-testid="portal-drawer"
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 20px", borderBottom: "1px solid var(--folk-border)" }}>
              <div style={{ fontSize: 12, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--folk-muted)", fontWeight: 600 }}>
                Your Folk Studio
              </div>
              <button type="button" data-testid="portal-drawer-close" onClick={() => setDrawerOpen(false)} className="folk-btn" style={{ padding: "6px 10px" }}>×</button>
            </div>
            <SidebarNav
              groups={SECTION_GROUPS}
              section={section}
              setSection={(k) => { setSection(k); setDrawerOpen(false); }}
            />
          </aside>
        </div>
      )}

      <main className="folk-main" style={{ maxWidth: 1080, margin: "0 auto", padding: "40px 24px 96px" }}>
        {section !== "status" && <ProjectTimelineBar data={data} onGoToStatus={() => setSection("status")} />}
        {section === "report" && (
          <ReportSection report={report} salutation={salutation} submissionId={id} />
        )}
        {section === "welcome" && (
          <>
            <StyleAtGlanceCard answers={data?.answers} />
            <FoundersSection salutation={salutation.salutation} letter={content.founders_letter} />
            <div style={{ marginTop: 32 }}>
              <ShareLinkBar url={`${window.location.origin}/portal/${id}`} />
            </div>
          </>
        )}
        {section === "status" && <StatusSection data={data} />}
        {section === "you" && <AboutYouSection data={data} />}
        {section === "home_details" && <HomeDetailsSection data={data} onUpdated={(next) => setData((d) => ({ ...d, ...next }))} />}
        {section === "billing" && <BillingSection report={report} data={data} />}
        {section === "process" && <DesignProcessSection override={content.design_process} />}
        {section === "roadmap" && <RoadmapSection roadmapContent={content.roadmap} />}
        {section === "together" && <WorkingTogetherSection override={content.working_together} />}
        {section === "terms" && <TermsSection terms={content.terms} />}
        {section === "project_moodboard" && <ProjectMoodboardSection data={data} />}
        {section === "your_designer" && <YourDesignerSection submissionId={submissionId} data={data} />}
        {section === "style_refs" && <StyleReferencesSection data={data} />}
        {section === "sketches" && (
          <UploadPlaceholderSection
            eyebrow="Sketches"
            chapter="10b"
            hue="blue"
            kind="sketches"
            data={data}
            title="Hand drawings, quick studies, first ideas."
            body="Every sketch our team puts to paper (or napkin) lives here — plan doodles, elevation studies, furniture drawings, paint-swatch layouts. Drop your own quick sketches too, we love them."
            note="No sketches here yet. Your team will drop the first ones as we begin the design phase."
          />
        )}
        {section === "renders" && (
          <UploadPlaceholderSection
            eyebrow="Project renders"
            chapter="09"
            hue="blue"
            kind="renders"
            data={data}
            title="Renders for your home, as they come to life."
            body="This is where every render for your home will land, the moment your Folk team finishes them, and the moment you spot inspiration too. Both sides can drop things in as the project unfolds."
            note="No renders here yet. Drop images above or ask your Folk team. Everything you upload is instantly visible to your project team."
          />
        )}
        {section === "layouts" && (
          <UploadPlaceholderSection
            eyebrow="Layout library"
            chapter="10"
            hue="blue"
            kind="layouts"
            data={data}
            title="Every plan, elevation and layout — one shared library."
            body="Floor plans, elevations, sample photos and material specs live here, updated as our team develops them. You can upload too: sketches, references, layouts you loved from other homes."
            note="No layouts here yet. Drop PDFs or images above. Your Folk team will review anything you add."
          />
        )}
        {section === "custom_furniture" && (
          <UploadPlaceholderSection
            eyebrow="Custom furniture"
            chapter="11"
            hue="maroon"
            kind="custom_furniture"
            data={data}
            title="Bespoke pieces, drawn and detailed for you."
            body="Every custom design our team makes for you — beds, sofas, cabinetry, dining tables, side pieces — with drawings, dimensions and material notes."
            note="Nothing here yet. Custom pieces will appear once your design phase begins. You can also drop references and sketches you love."
          />
        )}
        {section === "materials_finishes" && (
          <UploadPlaceholderSection
            eyebrow="Materials & finishes"
            chapter="12"
            hue="yellow"
            kind="materials_finishes"
            data={data}
            title="Wood, stone, brass, tile — every material in one place."
            body="Photos and samples of every finish being considered for your home. As the team locks in choices, they'll be marked so you always know what's selected."
            note="No materials here yet. Your team will start dropping options once the design phase kicks off."
          />
        )}
        {section === "paint_textures" && (
          <UploadPlaceholderSection
            eyebrow="Paint & textures"
            chapter="13"
            hue="blue"
            kind="paint_textures"
            data={data}
            title="Paint colours, plasters and wall textures."
            body="Every colour swatch, paint chip and wall texture being explored. Test patches from site can go here too."
            note="No paint or textures here yet. We'll build this out as your colour palette firms up."
          />
        )}
        {section === "upholstery" && (
          <UploadPlaceholderSection
            eyebrow="Upholstery & furnishings"
            chapter="14"
            hue="maroon"
            kind="upholstery"
            data={data}
            title="Fabrics, curtains, cushions, rugs."
            body="Every fabric being considered for your custom pieces, curtains, drapes, upholstery and soft furnishings. Swatch photos, patterns, weaves — anything the team is testing."
            note="No fabrics here yet. This fills up once we get into the refinement phase."
          />
        )}
        {section === "objects" && <ObjectsMarketplaceSection submissionId={id} />}
        {section === "refer" && <ReferAFriendSection submissionId={id} salutation={salutation} />}
      </main>
      </div>

      <footer style={{ maxWidth: 1080, margin: "0 auto", padding: "0 24px 40px", color: "var(--folk-muted)", fontSize: 13, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>Private &amp; confidential. Folk, {new Date().getFullYear()}</div>
        <Link to="/" style={{ color: "var(--folk-accent)" }}>Back to Folk Objects</Link>
      </footer>
    </div>
  );
}

/* ─────────── Sidebar navigation ─────────── */
const SIDEBAR_STORAGE_KEY = "folk.portal.sidebarGroups.v1";

function loadCollapsedGroups() {
  try {
    const raw = localStorage.getItem(SIDEBAR_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function SidebarNav({ groups, section, setSection }) {
  // Which group contains the active item — that one must always be open.
  const activeGroup = useMemo(
    () => groups.find((g) => g.items.some((it) => it.key === section))?.label,
    [groups, section]
  );

  const [collapsed, setCollapsed] = useState(() => loadCollapsedGroups());

  // Keep the active group visible on section change.
  useEffect(() => {
    if (!activeGroup) return;
    setCollapsed((prev) => (prev[activeGroup] ? { ...prev, [activeGroup]: false } : prev));
  }, [activeGroup]);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, JSON.stringify(collapsed));
    } catch {
      /* storage unavailable, ignore */
    }
  }, [collapsed]);

  const toggleGroup = (label) => {
    setCollapsed((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  return (
    <div className="folk-sidebar__inner">
      {groups.map((group) => {
        const isCollapsed = !!collapsed[group.label];
        const groupSlug = group.label.replace(/\s+/g, "-").toLowerCase();
        return (
          <div
            key={group.label}
            className={`folk-sidebar__group${isCollapsed ? " is-collapsed" : ""}`}
            data-testid={`nav-group-${groupSlug}`}
          >
            <button
              type="button"
              className="folk-sidebar__group-label"
              data-testid={`nav-group-toggle-${groupSlug}`}
              aria-expanded={!isCollapsed}
              onClick={() => toggleGroup(group.label)}
            >
              <span>{group.label}</span>
              <span aria-hidden className="folk-sidebar__chevron">
                {isCollapsed ? "+" : "−"}
              </span>
            </button>
            <div className="folk-sidebar__group-items" hidden={isCollapsed}>
              {group.items.map((item) => {
                const active = section === item.key;
                const hue = SECTION_HUES[item.hue] || SECTION_HUES.maroon;
                return (
                  <button
                    key={item.key}
                    type="button"
                    data-testid={`nav-${item.key}`}
                    data-sidebar-item={item.key}
                    onClick={() => setSection(item.key)}
                    className={`folk-sidebar__item${active ? " is-active" : ""}`}
                    style={{
                      background: active ? hue.bg : "transparent",
                      color: active ? hue.text : "#2A2828",
                      borderLeftColor: active ? hue.text : "transparent",
                      fontWeight: active ? 600 : 400,
                    }}
                  >
                    <span
                      aria-hidden
                      className="folk-sidebar__dot"
                      style={{ background: active ? hue.text : "var(--folk-accent-2)" }}
                    />
                    {item.label}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}



/* ─────────── Sections ─────────── */

function SectionHeader({ chapter, eyebrow, title, hue = "maroon" }) {
  const h = SECTION_HUES[hue] || SECTION_HUES.maroon;
  return (
    <div style={{ marginBottom: 28 }}>
      {chapter && (
        <div
          style={{
            display: "inline-block",
            padding: "6px 12px",
            borderRadius: 999,
            background: h.bg,
            color: h.text,
            fontSize: 11.5,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            fontWeight: 700,
            marginBottom: 12,
          }}
        >
          Chapter {chapter}
        </div>
      )}
      <div className="folk-section" style={{ marginBottom: 8, color: h.text }}>{eyebrow}</div>
      <h2 className="folk-h2" style={{ maxWidth: 780 }}>{title}</h2>
    </div>
  );
}

function FoundersSection({ salutation, letter }) {
  const paras = [
    `${salutation},`,
    letter?.opening || "",
    ...(letter?.paragraphs || []),
    letter?.signature || "",
  ].filter(Boolean);
  return (
    <section className="folk-fade-in">
      <SectionHeader chapter="01" eyebrow="A Note from the Founders" title="Welcome to Folk." hue="yellow" />
      <div
        data-testid="founders-letter"
        style={{
          background: "var(--folk-card)",
          border: "1.5px solid var(--folk-border)",
          borderRadius: 20,
          padding: 32,
          maxWidth: 780,
          display: "flex",
          flexDirection: "column",
          gap: 16,
          fontSize: 16,
          lineHeight: 1.75,
          color: "#2A2828",
        }}
      >
        {paras.map((p, i) => (
          <p key={i} style={{ margin: 0, whiteSpace: "pre-wrap" }}>{p}</p>
        ))}
      </div>
    </section>
  );
}

function StyleAssessmentBlock({ assessment, generating }) {
  if (!assessment) {
    return (
      <div data-testid="report-assessment-loading" style={{ background: "var(--folk-card)", border: "1.5px dashed var(--folk-border)", borderRadius: 18, padding: 22, color: "var(--folk-muted)", maxWidth: 820, fontSize: 14.5, lineHeight: 1.6 }}>
        {generating
          ? "our design team is running your style assessment now — palette, textures, do's and don'ts land here in a moment."
          : "your style assessment will appear here once your persona is finalised."}
      </div>
    );
  }
  const palette = assessment.palette || [];
  const textures = assessment.textures || [];
  const doThis = assessment.do_this || [];
  const avoid = assessment.avoid_this || [];
  const keywords = assessment.keywords || [];

  const eyebrow = { fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-accent)", fontWeight: 700, marginBottom: 12 };
  const chip = { display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 999, border: "1.5px solid var(--folk-accent)", color: "var(--folk-accent)", fontFamily: "Host Grotesk, sans-serif", fontSize: 13, background: "transparent" };
  const bullet = { display: "flex", gap: 10, alignItems: "flex-start", fontFamily: "Host Grotesk, sans-serif", fontSize: 15, lineHeight: 1.55, color: "#2A2828" };

  return (
    <div data-testid="report-assessment" style={{ display: "flex", flexDirection: "column", gap: 24, maxWidth: 820 }}>
      {palette.length > 0 && (
        <div data-testid="report-assessment-palette" style={{ background: "var(--folk-card)", border: "1.5px solid var(--folk-border)", borderRadius: 20, padding: 24 }}>
          <div style={eyebrow}>Your palette</div>
          <div style={{ fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: "-0.028em", fontSize: 24, textTransform: "lowercase", lineHeight: 1.2, marginBottom: 16 }}>the colours we heard in your answers.</div>
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(palette.length, 6)}, minmax(0, 1fr))`, gap: 10 }}>
            {palette.map((c, i) => (
              <div key={i} data-testid={`palette-swatch-${i}`}>
                <div title={c.hex} style={{ aspectRatio: "1 / 1.05", borderRadius: 14, border: "1.5px solid var(--folk-border)", background: c.hex }} />
                <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 12.5, color: "#2A2828", marginTop: 8, textTransform: "lowercase" }}>{c.name}</div>
                <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 11.5, color: "var(--folk-muted)", letterSpacing: "0.04em" }}>{c.hex}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {(textures.length > 0 || keywords.length > 0) && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16 }}>
          {textures.length > 0 && (
            <div data-testid="report-assessment-textures" style={{ background: "var(--folk-card)", border: "1.5px solid var(--folk-border)", borderRadius: 20, padding: 24 }}>
              <div style={eyebrow}>Materials & textures</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {textures.map((t, i) => <span key={i} style={chip}>{t.toLowerCase()}</span>)}
              </div>
            </div>
          )}
          {keywords.length > 0 && (
            <div data-testid="report-assessment-keywords" style={{ background: "var(--folk-card)", border: "1.5px solid var(--folk-border)", borderRadius: 20, padding: 24 }}>
              <div style={eyebrow}>Keywords we're carrying forward</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {keywords.map((k, i) => <span key={i} style={{ ...chip, borderColor: "#0F3A54", color: "#0F3A54" }}>{k.toLowerCase()}</span>)}
              </div>
            </div>
          )}
        </div>
      )}

      {(doThis.length > 0 || avoid.length > 0) && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
          {doThis.length > 0 && (
            <div data-testid="report-assessment-do" style={{ background: "#F1F8EF", border: "1.5px solid rgba(28, 79, 44, 0.25)", borderRadius: 20, padding: 24 }}>
              <div style={{ ...eyebrow, color: "#1C4F2C" }}>Lean into this</div>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 10 }}>
                {doThis.map((d, i) => (
                  <li key={i} style={bullet}>
                    <span aria-hidden style={{ color: "#1C4F2C", fontWeight: 700, marginTop: 1 }}>+</span>
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {avoid.length > 0 && (
            <div data-testid="report-assessment-avoid" style={{ background: "#FCEFE9", border: "1.5px solid rgba(104, 40, 14, 0.25)", borderRadius: 20, padding: 24 }}>
              <div style={{ ...eyebrow, color: "#68280E" }}>Steer clear of</div>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 10 }}>
                {avoid.map((a, i) => (
                  <li key={i} style={bullet}>
                    <span aria-hidden style={{ color: "#68280E", fontWeight: 700, marginTop: 1 }}>·</span>
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const RAW_TO_LABEL = {
  styles: "Preferred styles",
  colorRelationship: "Color relationship",
  furniture: "Furniture feel",
  pattern: "Solids or patterns",
  motif: "Florals or geometrics",
  rug: "Rug preference",
  lighting: "Lighting mood",
  walls: "Walls",
};

function deriveRawAnswers(raw, labeled) {
  if (raw && Object.keys(raw).length > 0) return raw;
  if (!labeled) return {};
  const out = {};
  for (const [rawKey, label] of Object.entries(RAW_TO_LABEL)) {
    const v = labeled[label];
    if (v === undefined || v === null || v === "") continue;
    out[rawKey] = rawKey === "styles" && typeof v === "string" ? v.split(",").map((s) => s.trim()).filter(Boolean) : v;
  }
  return out;
}

function VisualSelectionsBlock({ answersRaw, answersLabeled }) {
  const answers = useMemo(() => deriveRawAnswers(answersRaw, answersLabeled), [answersRaw, answersLabeled]);
  const styles = Array.isArray(answers?.styles) ? answers.styles : [];
  const colorPick = answers?.colorRelationship;
  const chosenStyles = STYLE_DEFS.filter((d) => styles.includes(d.value));
  const chosenColor = COLOR_DEFS.find((c) => c.value === colorPick);
  const eitherOrs = PREF_DEFS
    .map((d) => {
      const val = answers?.[d.key];
      if (val !== "a" && val !== "b") return null;
      return {
        key: d.key,
        question: d.question,
        chosenSide: val,
        chosenLabel: val === "a" ? d.aLabel : d.bLabel,
        chosenImg: val === "a" ? d.aImg : d.bImg,
        otherLabel: val === "a" ? d.bLabel : d.aLabel,
        otherImg: val === "a" ? d.bImg : d.aImg,
      };
    })
    .filter(Boolean);

  if (chosenStyles.length === 0 && !chosenColor && eitherOrs.length === 0) return null;

  const eyebrow = { fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-accent)", fontWeight: 700, marginBottom: 10 };
  const heading = { fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: "-0.028em", fontSize: 24, textTransform: "lowercase", lineHeight: 1.2, marginBottom: 14 };

  return (
    <div data-testid="report-visual-selections" style={{ display: "flex", flexDirection: "column", gap: 20, maxWidth: 820 }}>
      {chosenStyles.length > 0 && (
        <div data-testid="report-visual-styles" style={{ background: "var(--folk-card)", border: "1.5px solid var(--folk-border)", borderRadius: 20, padding: 24 }}>
          <div style={eyebrow}>Styles you chose</div>
          <div style={heading}>the three aesthetics you gravitated to.</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {chosenStyles.map((d) => (
              <div key={d.value} data-testid={`visual-style-${d.value}`} style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(180px, 260px)", gap: 16, alignItems: "center" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, borderRadius: 12, overflow: "hidden" }}>
                  {[1, 2, 3, 4].map((n) => (
                    <div key={n} style={{ aspectRatio: "1/1", position: "relative", background: "#F3ECE9", overflow: "hidden" }}>
                      <img src={`/refs/style-${d.slot}-${n}.png`} alt={d.label} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                    </div>
                  ))}
                </div>
                <div>
                  <div style={{ fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: "-0.02em", fontSize: 20, color: "#1B1A1A", textTransform: "lowercase", marginBottom: 6 }}>{d.label.toLowerCase()}</div>
                  <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 13.5, color: "var(--folk-muted)", lineHeight: 1.55 }}>{d.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {chosenColor && (
        <div data-testid="report-visual-color" style={{ background: "var(--folk-card)", border: "1.5px solid var(--folk-border)", borderRadius: 20, padding: 24 }}>
          <div style={eyebrow}>Colour relationship</div>
          <div style={heading}>the mood you leaned into.</div>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(180px, 240px)", gap: 16, alignItems: "center" }}>
            <div style={{ display: "flex", gap: 6, height: 160, borderRadius: 12, overflow: "hidden" }}>
              {chosenColor.imgs ? (
                chosenColor.imgs.map((src, i) => (
                  <div key={i} style={{ flex: 1, overflow: "hidden", borderRadius: 8, background: "#F3ECE9" }}>
                    <img src={src} alt={chosenColor.label} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                  </div>
                ))
              ) : (
                chosenColor.swatches?.map((sw, i) => (
                  <div key={i} style={{ flex: 1, background: sw, borderLeft: i === 0 ? "none" : "1px solid var(--folk-border)" }} />
                ))
              )}
            </div>
            <div>
              <div style={{ fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: "-0.02em", fontSize: 20, color: "#1B1A1A", textTransform: "lowercase", marginBottom: 10 }}>{chosenColor.label.toLowerCase()}</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {chosenColor.swatches?.map((sw, i) => (
                  <div key={i} title={sw} style={{ width: 30, height: 30, borderRadius: 8, background: sw, border: sw === "#FFFFFF" ? "1.5px solid var(--folk-border)" : "1.5px solid rgba(27, 26, 26, 0.06)" }} />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {eitherOrs.length > 0 && (
        <div data-testid="report-visual-either-or" style={{ background: "var(--folk-card)", border: "1.5px solid var(--folk-border)", borderRadius: 20, padding: 24 }}>
          <div style={eyebrow}>Your either-or picks</div>
          <div style={heading}>the little details you decided on.</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 }}>
            {eitherOrs.map((eo) => (
              <div key={eo.key} data-testid={`visual-eo-${eo.key}`} style={{ background: "var(--folk-bg)", border: "1.5px solid var(--folk-border)", borderRadius: 14, overflow: "hidden", display: "flex", flexDirection: "column" }}>
                <div style={{ aspectRatio: "4/3", background: "#F3ECE9", overflow: "hidden" }}>
                  <img src={eo.chosenImg} alt={eo.chosenLabel} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                </div>
                <div style={{ padding: "12px 14px" }}>
                  <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-hint)", fontWeight: 600, marginBottom: 4 }}>{eo.question}</div>
                  <div style={{ fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: "-0.02em", fontSize: 17, textTransform: "lowercase", color: "#1B1A1A" }}>{eo.chosenLabel.toLowerCase()}</div>
                  <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 11.5, color: "var(--folk-muted)", marginTop: 4 }}>
                    over <span style={{ textDecoration: "line-through" }}>{eo.otherLabel.toLowerCase()}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ShareReportPanel({ submissionId }) {
  const [links, setLinks] = useState([]);
  const [busy, setBusy] = useState(false);
  const [copiedToken, setCopiedToken] = useState(null);

  const load = React.useCallback(async () => {
    if (!submissionId) return;
    try {
      const res = await axios.get(`${API}/portal/${submissionId}/share-links`);
      setLinks(res.data?.links || []);
    } catch { /* silent */ }
  }, [submissionId]);

  useEffect(() => { load(); }, [load]);

  const createLink = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/portal/${submissionId}/share-link`, {});
      await load();
    } catch { /* silent */ }
    finally { setBusy(false); }
  };

  const copy = async (url, token) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(null), 1800);
    } catch { /* clipboard blocked */ }
  };

  const revoke = async (token) => {
    if (!window.confirm("Revoke this share link? Anyone who has it will lose access.")) return;
    try {
      await axios.post(`${API}/portal/${submissionId}/share-link/${token}/revoke`, {});
      await load();
    } catch { /* silent */ }
  };

  return (
    <div data-testid="report-share-panel" style={{ background: "#BADDF0", border: "1.5px solid rgba(15, 58, 84, 0.24)", borderRadius: 20, padding: 26, maxWidth: 820 }}>
      <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "#0F3A54", fontWeight: 700, marginBottom: 8 }}>Share your report</div>
      <div style={{ fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: "-0.028em", fontSize: 24, textTransform: "lowercase", color: "#0F3A54", lineHeight: 1.2, marginBottom: 8 }}>
        pass this along to your partner or family.
      </div>
      <p style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 14.5, lineHeight: 1.6, color: "#0F3A54", margin: 0 }}>
        We'll mint a read-only link that stays live for seven days. Anyone with it can see your persona, palette and moodboard — but nothing here can be changed.
      </p>

      {links.length === 0 ? (
        <button
          type="button"
          onClick={createLink}
          disabled={busy}
          data-testid="report-share-create"
          className="folk-pill"
          style={{ marginTop: 18, background: "#0F3A54", color: "#FFFBC9", border: "1.5px solid #0F3A54", padding: "10px 20px", fontSize: 14, opacity: busy ? 0.55 : 1 }}
        >
          {busy ? "Creating…" : "Create a share link"}
        </button>
      ) : (
        <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 10 }}>
          {links.map((l) => {
            const expiresLabel = formatShareExpiry(l.expires_at);
            return (
              <div key={l.token} data-testid={`report-share-link-${l.token}`} style={{ background: "#FFF", borderRadius: 12, padding: 12, border: "1.5px solid rgba(15, 58, 84, 0.2)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
                  <input
                    readOnly
                    value={l.url}
                    onFocus={(e) => e.target.select()}
                    data-testid={`report-share-url-${l.token}`}
                    style={{ flex: 1, minWidth: 200, background: "transparent", border: "1.5px dashed rgba(15, 58, 84, 0.25)", borderRadius: 8, padding: "8px 10px", fontFamily: "Host Grotesk, sans-serif", fontSize: 13, color: "#0F3A54" }}
                  />
                  <button
                    type="button"
                    onClick={() => copy(l.url, l.token)}
                    data-testid={`report-share-copy-${l.token}`}
                    className="folk-pill"
                    style={{ padding: "8px 14px", fontSize: 12.5, background: copiedToken === l.token ? "#1C4F2C" : "#0F3A54", color: "#FFF", border: `1.5px solid ${copiedToken === l.token ? "#1C4F2C" : "#0F3A54"}` }}
                  >
                    {copiedToken === l.token ? "Copied ✓" : "Copy link"}
                  </button>
                  <button
                    type="button"
                    onClick={() => revoke(l.token)}
                    data-testid={`report-share-revoke-${l.token}`}
                    style={{ background: "transparent", border: "none", color: "#68280E", cursor: "pointer", fontSize: 12, textDecoration: "underline", fontFamily: "Host Grotesk, sans-serif" }}
                  >
                    Revoke
                  </button>
                </div>
                <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 11.5, color: "#0F3A54" }}>
                  Expires {expiresLabel} · {l.view_count || 0} view{l.view_count === 1 ? "" : "s"}
                </div>
              </div>
            );
          })}
          <button
            type="button"
            onClick={createLink}
            disabled={busy}
            data-testid="report-share-create-more"
            style={{ alignSelf: "flex-start", background: "transparent", border: "none", color: "#0F3A54", cursor: "pointer", fontSize: 13, textDecoration: "underline", fontFamily: "Host Grotesk, sans-serif" }}
          >
            {busy ? "Creating…" : "+ create another link"}
          </button>
        </div>
      )}
    </div>
  );
}

function formatShareExpiry(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const days = Math.ceil((d.getTime() - Date.now()) / (24 * 60 * 60 * 1000));
    if (days <= 0) return "today";
    if (days === 1) return "tomorrow";
    return `in ${days} days`;
  } catch { return ""; }
}

function ReportSection({ report, salutation, submissionId }) {
  const first = salutation.firstNames?.[0] || salutation.displayName || "there";
  const [assessment, setAssessment] = useState(report?.style_assessment || null);
  const [generatingAssessment, setGeneratingAssessment] = useState(false);
  const [zoomPin, setZoomPin] = useState(null);
  useEffect(() => {
    setAssessment(report?.style_assessment || null);
  }, [report?.style_assessment]);
  useEffect(() => {
    // Auto-generate the assessment once persona is ready and we don't have one yet.
    if (!report?.persona_summary || assessment || generatingAssessment || !submissionId) return;
    setGeneratingAssessment(true);
    axios
      .post(`${API}/portal/${submissionId}/generate-style-assessment`, {})
      .then((res) => setAssessment(res.data?.style_assessment || null))
      .catch(() => { /* silent — retry on next visit */ })
      .finally(() => setGeneratingAssessment(false));
  }, [report?.persona_summary, assessment, generatingAssessment, submissionId]);
  const openMessage = () => {
    try { window.dispatchEvent(new CustomEvent("folk:open-leave-message")); } catch { /* noop */ }
  };
  const payAdvance = () => { /* removed — team handles advance out-of-band for now */ };

  if (!report) {
    return (
      <section className="folk-fade-in" data-testid="report-loading">
        <SectionHeader eyebrow="Your report" title={first ? `${first}, we've been reading you.` : "Your report"} />
        <div style={{ background: "var(--folk-card)", border: "1.5px solid var(--folk-border)", borderRadius: 18, padding: 28, color: "var(--folk-muted)", maxWidth: 780 }}>
          Building your report…
        </div>
      </section>
    );
  }

  const tiers = report.tiers || [];
  const recommended = tiers.find((t) => t.id === report.recommended_tier_id)
    || tiers.find((t) => t.is_recommended)
    || tiers[0];
  const boards = report.moodboards || [];
  const pinsByBoard = (report.moodboard_pins || []).reduce((acc, p) => {
    (acc[p.board_id] = acc[p.board_id] || []).push(p);
    return acc;
  }, {});
  const contact = report.contact || {};
  const persona = report.persona_summary || "Your Folk persona is being written up — refresh in a moment.";

  return (
    <section className="folk-fade-in" data-testid="report-section" style={{ display: "flex", flexDirection: "column", gap: 36 }}>
      <SectionHeader
        eyebrow="Your Folk report"
        title={first ? `${first}, this is what we heard.` : "This is what we heard."}
      />

      {/* Persona */}
      <div data-testid="report-persona" style={{ background: "#FFFBC9", border: "1.5px solid rgba(104, 40, 14, 0.18)", borderRadius: 20, padding: 28, maxWidth: 820 }}>
        <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "#68280E", fontWeight: 700, marginBottom: 12 }}>Who you are, to us</div>
        <div style={{ fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: "-0.015em", fontSize: 17.5, lineHeight: 1.7, color: "#1B1A1A", whiteSpace: "pre-wrap" }}>
          {persona}
        </div>
      </div>

      {/* Designer handoff card (only shows when a designer is assigned) */}
      <DesignerHandoffCard
        submissionId={submissionId}
        onOpen={() => {
          const el = document.querySelector('[data-sidebar-item="your_designer"]');
          if (el) el.click();
        }}
      />

      {/* Style assessment (palette / textures / do / avoid / keywords) */}
      <StyleAssessmentBlock assessment={assessment} generating={generatingAssessment} />

      {/* Curated moodboards */}
      <div data-testid="report-moodboards">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-accent)", fontWeight: 700 }}>Moodboards, curated for you</div>
            <div style={{ fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: "-0.028em", fontSize: 26, lineHeight: 1.2, textTransform: "lowercase", marginTop: 4 }}>a first look at how your home wants to feel.</div>
          </div>
          <a href={`/portal/${submissionId}#project_moodboard`} onClick={(e) => { e.preventDefault(); const el = document.querySelector('[data-sidebar-item="project_moodboard"]'); if (el) el.click(); }} style={{ fontSize: 13, color: "var(--folk-accent)", textDecoration: "underline", fontFamily: "Host Grotesk, sans-serif" }} data-testid="report-open-moodboards">
            Open full moodboard →
          </a>
        </div>
        {boards.length === 0 ? (
          <div style={{ background: "var(--folk-card)", border: "1px dashed var(--folk-border)", borderRadius: 16, padding: 22, color: "var(--folk-muted)", fontSize: 14.5, lineHeight: 1.55, maxWidth: 820 }}>
            Your Folk team hasn't published a curated moodboard yet — they'll drop the first one within a business day or two.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
            {boards.map((b) => {
              const pins = (pinsByBoard[b.id] || []).slice(0, 6);
              return (
                <div key={b.id} data-testid={`report-board-${b.id}`} style={{ background: "var(--folk-card)", border: "1.5px solid var(--folk-border)", borderRadius: 18, padding: 18 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
                    <div style={{ fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: "-0.028em", fontSize: 20, textTransform: "lowercase" }}>{(b.name || "moodboard").toLowerCase()}</div>
                    <div style={{ fontSize: 12, color: "var(--folk-muted)", fontFamily: "Host Grotesk, sans-serif" }}>{pins.length ? `${(pinsByBoard[b.id] || []).length} images` : "empty"}</div>
                  </div>
                  {pins.length === 0 ? (
                    <div style={{ color: "var(--folk-muted)", fontSize: 14 }}>No pins on this board yet.</div>
                  ) : (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(320px, 100%), 1fr))", gap: 12 }}>
                      {pins.map((p) => (
                        <div key={p.id} style={{ borderRadius: 12, overflow: "hidden", background: "#EFE5E1", cursor: "zoom-in" }} onClick={() => setZoomPin(p)} data-testid={`report-pin-${p.id}`}>
                          <img src={p.image_url} alt={p.note || "pin"} loading="lazy" style={{ width: "100%", height: "auto", display: "block", objectFit: "cover" }} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {zoomPin && (
        <ImageLightbox
          src={zoomPin.image_url}
          alt={zoomPin.note || ""}
          onClose={() => setZoomPin(null)}
        />
      )}

      {/* Style selections captured in the survey */}
      <VisualSelectionsBlock
        answersRaw={report?.answers_raw}
        answersLabeled={report?.answers}
      />

      {/* Share the report */}
      <ShareReportPanel submissionId={submissionId} />

      {/* CTAs */}
      <div data-testid="report-ctas" style={{ background: "var(--folk-card)", border: "1.5px solid var(--folk-border)", borderRadius: 20, padding: 28, maxWidth: 820 }}>
        <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-accent)", fontWeight: 700, marginBottom: 8 }}>What happens next</div>
        <div style={{ fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: "-0.028em", fontSize: 26, textTransform: "lowercase", lineHeight: 1.2, marginBottom: 8 }}>ready to build with us, or want to ask a few things first?</div>
        <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 15, lineHeight: 1.6, color: "#2A2828", marginBottom: 20 }}>
          {report.advance_paid
            ? `Your advance for ${report.paid_tier?.name || 'the recommended plan'} is in — your project is officially live. We'll be in touch shortly.`
            : "Ready to move? Leave us a note or ping us on WhatsApp and we'll walk you through the next step."}
        </div>
        {!report.advance_paid && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            <button
              type="button"
              data-testid="report-leave-message"
              onClick={openMessage}
              className="folk-pill"
              style={{ background: "#68280E", color: "#FFF", border: "1.5px solid #68280E", padding: "12px 22px", fontSize: 15 }}
            >
              Leave us a message
            </button>
            {contact.whatsapp_url && (
              <a
                href={contact.whatsapp_url}
                target="_blank"
                rel="noreferrer"
                data-testid="report-whatsapp"
                className="folk-pill"
                style={{ background: "#25D366", color: "#FFF", border: "1.5px solid #1DA851", padding: "12px 22px", fontSize: 15, textDecoration: "none" }}
              >
                WhatsApp {contact.whatsapp_display}
              </a>
            )}
            {contact.email && (
              <a
                href={`mailto:${contact.email}?subject=${encodeURIComponent("A question about my Folk report")}`}
                data-testid="report-email"
                className="folk-pill"
                style={{ background: "transparent", color: "#68280E", border: "1.5px solid #68280E", padding: "12px 22px", fontSize: 15, textDecoration: "none" }}
              >
                Email {contact.email}
              </a>
            )}
          </div>
        )}
      </div>
    </section>
  );
}


function PersonaSection({ data, generating, salutation }) {
  const first = salutation.firstNames?.[0];
  return (
    <section className="folk-fade-in">
      <SectionHeader
        eyebrow="A note from your design team"
        title={first ? `${first}, we've been reading you.` : "We've been reading you."}
      />
      {data.persona_summary ? (
        <div
          data-testid="portal-summary"
          style={{
            background: "var(--folk-card)",
            border: "1.5px solid var(--folk-border)",
            borderRadius: 18,
            padding: 28,
            fontSize: 16,
            lineHeight: 1.7,
            color: "#2A2828",
            whiteSpace: "pre-wrap",
            maxWidth: 780,
          }}
        >
          {data.persona_summary}
        </div>
      ) : (
        <div
          data-testid="portal-summary-loading"
          style={{
            background: "var(--folk-card)",
            border: "1.5px dashed var(--folk-border)",
            borderRadius: 18,
            padding: 28,
            color: "var(--folk-muted)",
            maxWidth: 780,
          }}
        >
          {generating ? "Composing your letter…" : "Your letter will appear here shortly."}
        </div>
      )}
    </section>
  );
}

function BillingSection({ report, data }) {
  const rateCardUrl = report?.rate_card_pdf_url || null;
  const [requesting, setRequesting] = useState(false);
  const [requestedAt, setRequestedAt] = useState(null);
  const submissionId = data?.id;

  const requestRateCard = async () => {
    if (requesting || !submissionId) return;
    setRequesting(true);
    try {
      await axios.post(`${API}/portal/${submissionId}/queries`, {
        subject: "Rate card request",
        message: "Hi Folk team, could you please share the latest rate card with me? Thanks!",
        email: report?.contact_email || data?.contact_email || null,
      });
      setRequestedAt(new Date().toISOString());
    } catch { /* silent */ }
    finally { setRequesting(false); }
  };

  const openMessage = () => {
    try { window.dispatchEvent(new CustomEvent("folk:open-leave-message")); } catch { /* noop */ }
  };

  return (
    <section className="folk-fade-in" data-testid="billing-section" style={{ display: "flex", flexDirection: "column", gap: 26, maxWidth: 860 }}>
      <SectionHeader
        chapter="01b"
        eyebrow="Billing"
        title="how you'll pay, and what unlocks when."
        hue="maroon"
      />

      {/* Rate card block */}
      <div
        data-testid="billing-rate-card"
        style={{
          background: "var(--folk-card)",
          border: "1.5px solid var(--folk-border)",
          borderRadius: 20,
          padding: "26px 28px",
          display: "flex", flexDirection: "column", gap: 14,
        }}
      >
        <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-accent)", fontWeight: 700 }}>The full rate card</div>
        <div style={{ fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: "-0.028em", fontSize: 26, textTransform: "lowercase", lineHeight: 1.2 }}>
          three ways to work with folk.
        </div>
        <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 15, lineHeight: 1.6, color: "#2A2828" }}>
          Every plan is spelled out in the rate card PDF, scope, inclusions, timelines, deliverables and pricing. Grab a copy or ask us to send it over.
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 4 }}>
          {rateCardUrl && (
            <a
              href={rateCardUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="billing-download-pdf"
              className="folk-pill"
              style={{ textDecoration: "none", background: "#68280E", color: "#FFF", border: "1.5px solid #68280E", padding: "12px 22px", fontSize: 15 }}
            >
              Download rate card PDF ↓
            </a>
          )}
          <button
            type="button"
            onClick={requestRateCard}
            disabled={requesting || !!requestedAt}
            data-testid="billing-request-rate-card"
            className="folk-pill"
            style={{ background: "#FFF", color: "#68280E", border: "1.5px solid #68280E", padding: "12px 22px", fontSize: 15 }}
          >
            {requestedAt ? "Request sent — check your inbox" : requesting ? "Sending…" : "Request rate card"}
          </button>
        </div>
      </div>

      {/* Design fees advance rule */}
      <div
        data-testid="billing-advance-rule"
        style={{
          background: "#FFFBC9",
          border: "1.5px solid rgba(104, 40, 14, 0.24)",
          borderRadius: 18,
          padding: "22px 24px",
          display: "flex", flexDirection: "column", gap: 10,
        }}
      >
        <div style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: "#68280E", fontWeight: 700 }}>Design fees</div>
        <div style={{ fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: "-0.028em", fontSize: 22, lineHeight: 1.25, textTransform: "lowercase", color: "#3E2A1E" }}>
          100% in advance to unlock your project.
        </div>
        <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 14.5, lineHeight: 1.65, color: "#3E2A1E" }}>
          Design fees are billed and cleared in full before we start. The second your advance lands, your live project timeline, team access and delivery windows unlock.
        </div>
      </div>

      {/* Product billing rebate */}
      <ProductRebateBlock rebate={report?.rebate} />

      {/* Have a billing question CTA */}
      <div
        style={{
          background: "var(--folk-card)",
          border: "1.5px solid var(--folk-border)",
          borderRadius: 18,
          padding: "20px 24px",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, flexWrap: "wrap",
        }}
      >
        <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 14.5, color: "#2A2828" }}>
          Something on your bill you'd like walked through? Drop us a note and we'll come back the same working day.
        </div>
        <button
          type="button"
          onClick={openMessage}
          data-testid="billing-leave-message"
          className="folk-pill"
          style={{ background: "#68280E", color: "#FFF", border: "1.5px solid #68280E", padding: "10px 20px", fontSize: 14 }}
        >
          Leave us a message
        </button>
      </div>
    </section>
  );
}

function ProductRebateBlock({ rebate }) {
  const r = rebate || {};
  const eligible = !!r.eligible_plan;
  const active = !!r.active;
  const inr = (n) => "₹" + (Number(n) || 0).toLocaleString("en-IN");
  const progress = Math.max(0, Math.min(100, Number(r.progress_pct || 0)));

  // 1) Not on a qualifying plan — keep the static explainer.
  if (!eligible) {
    return (
      <div
        data-testid="billing-product-rebate"
        style={{
          background: "#0F3A54",
          color: "#FFFBC9",
          border: "1.5px solid #0F3A54",
          borderRadius: 18,
          padding: "22px 24px",
          display: "flex", flexDirection: "column", gap: 10,
        }}
      >
        <div style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", fontWeight: 700, opacity: 0.88 }}>Product billing rebate · Folk Full & Turnkey</div>
        <div style={{ fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: "-0.028em", fontSize: 22, lineHeight: 1.25, textTransform: "lowercase" }}>
          bill more than ₹5L on platform, get an automatic 10% back.
        </div>
        <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 14.5, lineHeight: 1.65, opacity: 0.95 }}>
          On Folk Full and Turnkey projects, once your product billing on the Folk platform crosses ₹5,00,000, a 10% rebate on your total product billing activates automatically. No coupon, no request, it just kicks in.
        </div>
      </div>
    );
  }

  // 2) Qualifying plan — show live tracker (either "in progress" or "unlocked").
  const bgActive = "#123B21";     // deep folk green
  const bgTracking = "#0F3A54";   // folk navy
  const barBg = "rgba(255, 251, 201, 0.18)";
  const barFill = "#F0B458";      // brass

  return (
    <div
      data-testid={active ? "billing-rebate-unlocked" : "billing-rebate-tracking"}
      style={{
        background: active ? bgActive : bgTracking,
        color: "#FFFBC9",
        border: `1.5px solid ${active ? bgActive : bgTracking}`,
        borderRadius: 18,
        padding: "22px 24px",
        display: "flex", flexDirection: "column", gap: 14,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", fontWeight: 700, opacity: 0.9 }}>
          Product billing rebate · {r.plan_name || "Folk Full"}
        </span>
        {active && (
          <span
            data-testid="billing-rebate-badge"
            style={{
              fontSize: 10.5, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 800,
              padding: "4px 10px", borderRadius: 999,
              background: "#F0B458", color: "#3E2A1E",
            }}
          >
            unlocked
          </span>
        )}
      </div>

      <div style={{ fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: "-0.028em", fontSize: 22, lineHeight: 1.25, textTransform: "lowercase" }}>
        {active
          ? `you've unlocked ${inr(r.rebate_amount)} back, 10% of your product billing.`
          : "10% rebate kicks in the moment your product billing crosses ₹5L."}
      </div>

      <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 13.5, letterSpacing: "0.02em", display: "flex", justifyContent: "space-between", alignItems: "baseline", opacity: 0.95 }}>
        <span>{inr(r.product_billing || 0)} billed so far</span>
        <span>threshold {inr(r.threshold)}</span>
      </div>

      <div style={{ position: "relative", height: 8, background: barBg, borderRadius: 6, overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, width: `${progress}%`, background: barFill, borderRadius: 6, transition: "width 400ms ease" }} />
      </div>

      <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 13.5, lineHeight: 1.65, opacity: 0.92 }}>
        {active
          ? "This applies automatically to your total product billing on the Folk platform. No coupon, no claim, it just lands on your invoice."
          : `Keep going, ${(100 - progress).toFixed(0)}% to the threshold. Once you cross ₹5,00,000, 10% of your total product billing comes back to you automatically.`}
      </div>
    </div>
  );
}




function AboutYouSection({ data }) {
  const entries = Object.entries(data.answers || {});
  return (
    <section className="folk-fade-in">
      <SectionHeader eyebrow="Getting to know you" title="Your persona, in your own words." hue="yellow" />
      <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: 18 }}>
        <Link
          to={`/survey?edit=${data.id}`}
          data-testid="portal-edit-survey"
          className="folk-pill"
          style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8 }}
        >
          ✎ Update my answers
        </Link>
      </div>
      {entries.length === 0 ? (
        <div style={{ color: "var(--folk-muted)", maxWidth: 640 }}>
          You didn't share any answers this time. No worries. We'll cover this in our first working session.
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 14 }}>
          {entries.map(([k, v]) => (
            <div key={k} style={{ background: "var(--folk-card)", border: "1.5px solid var(--folk-border)", borderRadius: 14, padding: 16 }}>
              <div style={{ fontSize: 12, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--folk-hint)", marginBottom: 6 }}>{k}</div>
              <div style={{ fontSize: 15, color: "#2A2828", whiteSpace: "pre-wrap" }}>
                {Array.isArray(v) ? v.join(", ") : String(v)}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function HomeDetailsSection({ data, onUpdated }) {
  const [ownership, setOwnership] = useState(data?.home_ownership || "");
  const [finish, setFinish] = useState(data?.finish_state || "");
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  const OWNERSHIPS = [
    { key: "owned",  label: "Owned",  body: "It's ours — we can knock down walls and rebuild the whole thing if we want to." },
    { key: "rented", label: "Rented", body: "It's a rental — anything we do should be non-invasive or landlord-friendly." },
  ];
  const FINISHES = [
    { key: "builder_finish",  label: "Builder finish",  body: "Fully done. Walls, floors, kitchens, bathrooms — everything is in place." },
    { key: "bare_shell",      label: "Bare shell",      body: "Empty concrete. We're building from scratch — floors, walls, plumbing and all." },
    { key: "partially_done",  label: "Partially done",  body: "Somewhere in between. Some rooms finished, some raw. We'll fill the gaps together." },
  ];

  const save = async (nextOwnership, nextFinish) => {
    setSaving(true);
    try {
      const body = {
        home_ownership: nextOwnership || null,
        finish_state: nextOwnership === "rented" ? null : (nextFinish || null),
      };
      const { data: saved } = await axios.put(`${API}/portal/${data?.id}/home-details`, body);
      setSavedAt(new Date().toISOString());
      onUpdated && onUpdated({ home_ownership: saved.home_ownership, finish_state: saved.finish_state });
      if (saved.home_ownership === "rented") setFinish("");
    } catch { /* silent */ }
    finally { setSaving(false); }
  };

  const pickOwnership = (k) => {
    const next = ownership === k ? "" : k;
    setOwnership(next);
    save(next, finish);
  };
  const pickFinish = (k) => {
    const next = finish === k ? "" : k;
    setFinish(next);
    save(ownership, next);
  };

  return (
    <section className="folk-fade-in" data-testid="home-details-section" style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <SectionHeader chapter="01d" eyebrow="Home details" title="A few practical things about your place." hue="blue" />
      <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 15, lineHeight: 1.65, color: "#2A2828", maxWidth: 720 }}>
        These small facts shape everything we can and can't do inside your home. Update anytime, both your Folk team and you can edit here.
      </div>

      {/* Ownership */}
      <div data-testid="home-ownership-block" style={{ background: "var(--folk-card)", border: "1.5px solid var(--folk-border)", borderRadius: 18, padding: "22px 24px", maxWidth: 820 }}>
        <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "#0F3A54", fontWeight: 700, marginBottom: 14 }}>Is this home owned or rented?</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 10 }}>
          {OWNERSHIPS.map((o) => {
            const active = ownership === o.key;
            return (
              <button
                key={o.key}
                type="button"
                data-testid={`home-ownership-${o.key}`}
                onClick={() => pickOwnership(o.key)}
                disabled={saving}
                style={{
                  textAlign: "left",
                  background: active ? "#0F3A54" : "#FFF",
                  color: active ? "#FFFBC9" : "#1B1A1A",
                  border: `1.5px solid ${active ? "#0F3A54" : "var(--folk-border)"}`,
                  borderRadius: 14,
                  padding: "14px 16px",
                  cursor: "pointer",
                  fontFamily: "Host Grotesk, sans-serif",
                  transition: "background 180ms ease, color 180ms ease, border 180ms ease",
                }}
              >
                <div style={{ fontFamily: "Playfair Display, serif", letterSpacing: "-0.028em", fontSize: 20, textTransform: "lowercase", marginBottom: 4 }}>{o.label.toLowerCase()}</div>
                <div style={{ fontSize: 13.5, lineHeight: 1.55, opacity: active ? 0.9 : 0.75 }}>{o.body}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Finish state — shown for owned homes only */}
      {ownership === "owned" && (
        <div data-testid="home-finish-block" style={{ background: "var(--folk-card)", border: "1.5px solid var(--folk-border)", borderRadius: 18, padding: "22px 24px", maxWidth: 820 }}>
          <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "#0F3A54", fontWeight: 700, marginBottom: 14 }}>What state is the home in today?</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
            {FINISHES.map((f) => {
              const active = finish === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  data-testid={`home-finish-${f.key}`}
                  onClick={() => pickFinish(f.key)}
                  disabled={saving}
                  style={{
                    textAlign: "left",
                    background: active ? "#BADDF0" : "#FFF",
                    color: "#1B1A1A",
                    border: `1.5px solid ${active ? "#0F3A54" : "var(--folk-border)"}`,
                    borderRadius: 14,
                    padding: "14px 16px",
                    cursor: "pointer",
                    fontFamily: "Host Grotesk, sans-serif",
                    transition: "background 180ms ease, border 180ms ease",
                  }}
                >
                  <div style={{ fontFamily: "Playfair Display, serif", letterSpacing: "-0.028em", fontSize: 19, textTransform: "lowercase", marginBottom: 4 }}>{f.label.toLowerCase()}</div>
                  <div style={{ fontSize: 13, lineHeight: 1.55, color: "#5A5757" }}>{f.body}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 12, color: "var(--folk-hint)", minHeight: 16 }}>
        {saving ? "Saving…" : savedAt ? `Saved ${new Date(savedAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}` : "Tap any card to save."}
      </div>

      {/* Floor plans & layouts */}
      <UploadPlaceholderSection
        kind="home_layouts"
        data={data}
        embedded
        eyebrow="Floor plans & layouts"
        title="Share your floor plan"
        body="Upload your builder's floor plan, architect drawings or any layout you already have. PDFs and images welcome — they help us plan around real dimensions from day one."
        accept="image/*,application/pdf"
        dropHint="Drop floor plans / PDFs / images here, or click to select"
        maxMB={25}
      />

      {/* Photos & video walkthroughs */}
      <UploadPlaceholderSection
        kind="home_walkthroughs"
        data={data}
        embedded
        eyebrow="Photos & video walkthroughs"
        title="Show us the space"
        body="A quick phone walkthrough or a handful of photos of each room. Natural light, corners, quirks. The more we can see, the better we can design."
        accept="image/*,video/*"
        dropHint="Drop room photos or a walkthrough video here, or click to select"
        maxMB={100}
      />
    </section>
  );
}


function ReferAFriendSection({ submissionId, salutation }) {
  const [state, setState] = useState(null);
  const [copied, setCopied] = useState(false);
  const first = salutation.firstNames?.[0] || salutation.displayName || "there";

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/portal/${submissionId}/referrals`);
        if (mounted) setState(data);
      } catch { /* silent */ }
    };
    load();
    const iv = setInterval(load, 60000);
    return () => { mounted = false; clearInterval(iv); };
  }, [submissionId]);

  const copyLink = async () => {
    if (!state?.share_url) return;
    try {
      await navigator.clipboard.writeText(state.share_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { /* clipboard not available */ }
  };

  if (!state) {
    return (
      <section className="folk-fade-in" data-testid="refer-loading">
        <SectionHeader eyebrow="Refer a friend" title="Building your invite link…" hue="blue" />
      </section>
    );
  }

  const discount = state.discount_percentage || 0;
  const converted = state.referrals_converted || 0;
  const total = state.referrals_total || 0;

  return (
    <section className="folk-fade-in" data-testid="refer-section" style={{ display: "flex", flexDirection: "column", gap: 26, maxWidth: 820 }}>
      <SectionHeader
        chapter="01c"
        eyebrow="Refer a friend"
        title={discount > 0 ? `${discount}% off, unlocked.` : `${first}, share Folk. Get 10% back.`}
        hue="blue"
      />

      <div
        data-testid="refer-status"
        style={{
          background: discount > 0 ? "#0F3A54" : "#BADDF0",
          color: discount > 0 ? "#FFFBC9" : "#0F3A54",
          border: `1.5px solid ${discount > 0 ? "#0F3A54" : "rgba(15, 58, 84, 0.24)"}`,
          borderRadius: 20,
          padding: "24px 26px",
        }}
      >
        <div style={{ fontSize: 12, letterSpacing: "0.16em", textTransform: "uppercase", fontWeight: 700, marginBottom: 8, opacity: 0.85 }}>How it works</div>
        <div style={{ fontFamily: "Playfair Display, serif", letterSpacing: "-0.028em", fontSize: 22, lineHeight: 1.3, textTransform: "lowercase", marginBottom: 12 }}>
          {discount > 0
            ? `you've unlocked ${discount}% off your total folk billing.`
            : "when a friend signs up through your link, 10% off your total folk billing lands here."}
        </div>
        <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 14.5, lineHeight: 1.65 }}>
          Send them your personal invite. When they take the survey and pay their advance, we'll apply 10% off to your project, automatically, no strings, no expiry.
        </div>
        <div style={{ display: "flex", gap: 18, flexWrap: "wrap", marginTop: 18, fontFamily: "Host Grotesk, sans-serif", fontSize: 13 }}>
          <div><strong style={{ fontSize: 18, fontFamily: "Playfair Display, serif" }}>{total}</strong> invited</div>
          <div><strong style={{ fontSize: 18, fontFamily: "Playfair Display, serif" }}>{converted}</strong> signed up</div>
          <div><strong style={{ fontSize: 18, fontFamily: "Playfair Display, serif" }}>{discount}%</strong> discount unlocked</div>
        </div>
      </div>

      <div
        data-testid="refer-share"
        style={{
          background: "var(--folk-card)",
          border: "1.5px solid var(--folk-border)",
          borderRadius: 18,
          padding: "22px 24px",
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        <div style={{ fontSize: 12, letterSpacing: "0.16em", textTransform: "uppercase", color: "#0F3A54", fontWeight: 700 }}>Your invite link</div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <div
            data-testid="refer-share-url"
            style={{
              flex: 1, minWidth: 220,
              background: "#FAF5F3", border: "1px solid var(--folk-border)", borderRadius: 10,
              padding: "10px 14px", fontFamily: "Host Grotesk, sans-serif", fontSize: 13.5,
              color: "#1B1A1A", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}
          >
            {state.share_url}
          </div>
          <button
            type="button"
            data-testid="refer-copy-btn"
            onClick={copyLink}
            className="folk-pill"
            style={{ background: "#FFF", color: "#0F3A54", border: "1.5px solid #0F3A54", padding: "10px 18px", fontSize: 13 }}
          >
            {copied ? "Copied!" : "Copy link"}
          </button>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {state.whatsapp_url && (
            <a
              href={state.whatsapp_url}
              target="_blank"
              rel="noreferrer"
              data-testid="refer-whatsapp-btn"
              className="folk-pill"
              style={{ background: "#25D366", color: "#FFF", border: "1.5px solid #1DA851", padding: "12px 22px", fontSize: 14.5, textDecoration: "none", fontWeight: 600 }}
            >
              Share on WhatsApp
            </a>
          )}
          <a
            href={`mailto:?subject=${encodeURIComponent("A design studio worth a look — Folk")}&body=${encodeURIComponent(state.share_message || "")}`}
            data-testid="refer-email-btn"
            className="folk-pill"
            style={{ background: "transparent", color: "#0F3A54", border: "1.5px solid #0F3A54", padding: "12px 22px", fontSize: 14.5, textDecoration: "none" }}
          >
            Share by email
          </a>
        </div>
        <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 12.5, color: "var(--folk-muted)", lineHeight: 1.55 }}>
          Message preview: <em>"{state.share_message}"</em>
        </div>
      </div>

      {total > 0 && (
        <div data-testid="refer-list" style={{ background: "var(--folk-card)", border: "1.5px solid var(--folk-border)", borderRadius: 18, padding: "22px 24px" }}>
          <div style={{ fontSize: 12, letterSpacing: "0.16em", textTransform: "uppercase", color: "#0F3A54", fontWeight: 700, marginBottom: 12 }}>Friends you've invited</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {state.referrals.map((r) => (
              <div key={r.id} data-testid={`refer-row-${r.id}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "10px 14px", background: "#FAF5F3", borderRadius: 10, flexWrap: "wrap" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 2, fontFamily: "Host Grotesk, sans-serif" }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: "#1B1A1A" }}>{r.contact_name || "A quiet friend"}</div>
                  <div style={{ fontSize: 12, color: "var(--folk-muted)" }}>{r.contact_email || "—"}</div>
                </div>
                {r.converted ? (
                  <span style={{ background: "#0F3A54", color: "#FFFBC9", borderRadius: 999, padding: "4px 12px", fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700 }}>
                    Converted · +10%
                  </span>
                ) : (
                  <span style={{ background: "#FFFBC9", color: "#68280E", borderRadius: 999, padding: "4px 12px", fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700 }}>
                    Signed up
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}


const OBJECTS_COLLECTIONS = [
  { key: "",                    label: "everything" },
  { key: "all-lighting",        label: "lighting" },
  { key: "all-rugs",            label: "rugs" },
  { key: "all-decor",           label: "decor" },
  { key: "all-furniture",       label: "furniture" },
  { key: "textiles",            label: "textiles" },
  { key: "all-mirrors",         label: "mirrors" },
  { key: "all-wall-art",        label: "wall art" },
  { key: "collectibles",        label: "collectibles" },
];

function formatINR(value) {
  if (value == null) return null;
  try {
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(value);
  } catch {
    return `₹${Math.round(value).toLocaleString("en-IN")}`;
  }
}

function LiveMatchResultsBlock({ matches }) {
  // Only render "render groups" that have at least one match.
  const groups = (matches || []).filter((g) => (g.matches || []).length > 0);
  if (groups.length === 0) return null;
  const inr = (v) => v ? (typeof v === "number" ? `₹${v.toLocaleString("en-IN")}` : v) : "";
  return (
    <div
      data-testid="objects-automatch-live"
      style={{
        background: "linear-gradient(135deg, #EAF3ED 0%, #F5F8FB 100%)",
        border: "1.5px solid rgba(28, 79, 44, 0.24)",
        borderRadius: 18,
        padding: "22px 24px",
        display: "flex", flexDirection: "column", gap: 22,
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: "#1C4F2C", fontWeight: 700, marginBottom: 4 }}>Auto-match · live</div>
          <div style={{ fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: "-0.028em", fontSize: 24, color: "#3E2A1E", textTransform: "lowercase", lineHeight: 1.2 }}>
            your finalised renders, matched to folk products.
          </div>
        </div>
        <span data-testid="automatch-live-pill" style={{ fontSize: 10.5, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 800, padding: "5px 12px", borderRadius: 999, background: "#1C4F2C", color: "#EAF3ED" }}>
          {groups.length} render{groups.length === 1 ? "" : "s"} matched
        </span>
      </div>

      {groups.map((g) => (
        <div key={g.render_id} style={{ display: "grid", gridTemplateColumns: "220px 1fr", gap: 18, alignItems: "stretch" }} data-testid={`live-render-${g.render_id}`}>
          <div style={{ position: "relative", borderRadius: 14, overflow: "hidden", background: "#F3ECE9", aspectRatio: "3 / 4", border: "1.5px solid rgba(28, 79, 44, 0.18)" }}>
            {g.render_url && <img src={g.render_url} alt={g.render_filename || "render"} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
            <span style={{ position: "absolute", top: 10, left: 10, background: "#3E2A1E", color: "#FFFBC9", padding: "4px 10px", borderRadius: 999, fontSize: 10.5, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700 }}>
              Your render
            </span>
            {(g.detected_objects || []).length > 0 && (
              <div style={{ position: "absolute", bottom: 8, left: 8, right: 8, display: "flex", gap: 4, flexWrap: "wrap" }}>
                {(g.detected_objects || []).slice(0, 3).map((o) => (
                  <span key={o} style={{ background: "rgba(255, 255, 255, 0.92)", color: "#3E2A1E", padding: "2px 8px", borderRadius: 999, fontSize: 10.5, fontWeight: 600, fontFamily: "Host Grotesk, sans-serif" }}>
                    {o}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 12 }}>
            {(g.matches || []).slice(0, 6).map((m) => (
              <a
                key={m.product_id}
                href={m.url || "#"}
                target="_blank"
                rel="noreferrer"
                data-testid={`live-product-${m.product_id}`}
                style={{ background: "#FFFFFF", border: "1.5px solid var(--folk-border)", borderRadius: 14, overflow: "hidden", display: "flex", flexDirection: "column", textDecoration: "none", color: "inherit", transition: "transform 160ms ease" }}
              >
                <div style={{ position: "relative", aspectRatio: "1 / 1", background: "#F3ECE9" }}>
                  {m.thumb && <img src={m.thumb} alt={m.name} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
                  <span data-testid="live-match-chip" style={{ position: "absolute", top: 8, left: 8, background: "#1C4F2C", color: "#EAF3ED", fontSize: 10.5, fontWeight: 800, letterSpacing: "0.06em", padding: "3px 9px", borderRadius: 999 }}>
                    {m.match_pct}% match
                  </span>
                </div>
                <div style={{ padding: "10px 12px", display: "flex", flexDirection: "column", gap: 6, minHeight: 92 }}>
                  <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 13, fontWeight: 600, color: "#2A2828", lineHeight: 1.3 }}>{m.name}</div>
                  {m.detected_object && (
                    <div style={{ fontSize: 11, color: "var(--folk-muted)", fontStyle: "italic" }}>matched to your {m.detected_object}</div>
                  )}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "auto" }}>
                    {m.price && <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 12.5, color: "#68280E", fontWeight: 700 }}>{inr(m.price)}</div>}
                    <div style={{ fontSize: 11, color: "#1C4F2C", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em" }}>Check out →</div>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}



function AutoMatchExplainerCard() {
  const mockMatches = [
    { name: "Ilford Lounger, teak", price: "₹68,400", match: 96, thumb: "https://cdn.shopify.com/s/files/1/0577/9037/2419/files/OFC-Ilford-Chair-Front.jpg" },
    { name: "Halan Side Table, brass", price: "₹22,500", match: 93, thumb: "https://cdn.shopify.com/s/files/1/0577/9037/2419/files/OFC-Halan-Side-Table.jpg" },
    { name: "Kaya Floor Lamp, linen shade", price: "₹34,900", match: 91, thumb: "https://cdn.shopify.com/s/files/1/0577/9037/2419/files/OFC-Kaya-Lamp.jpg" },
  ];
  return (
    <div
      data-testid="objects-automatch-explainer"
      style={{
        background: "linear-gradient(135deg, #EAF3ED 0%, #F5F8FB 100%)",
        border: "1.5px solid rgba(28, 79, 44, 0.24)",
        borderRadius: 18,
        padding: "22px 24px",
        display: "flex", flexDirection: "column", gap: 18,
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: "#1C4F2C", fontWeight: 700, marginBottom: 4 }}>Auto-match · coming soon</div>
          <div style={{ fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: "-0.028em", fontSize: 24, color: "#3E2A1E", textTransform: "lowercase", lineHeight: 1.2 }}>
            every object in your renders, matched to folk products.
          </div>
        </div>
        <span data-testid="automatch-status-pill" style={{ fontSize: 10.5, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 800, padding: "5px 12px", borderRadius: 999, background: "#1C4F2C", color: "#EAF3ED" }}>
          Activates with your renders
        </span>
      </div>

      <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 14.5, lineHeight: 1.65, color: "#2A2828", maxWidth: 720 }}>
        Once your final renders land in the Renders tab, we'll scan each piece and surface the closest matches from the Folk Objects platform, at <strong>90% visual match or higher</strong>. Tap any card to check out directly, no back-and-forth with the team.
      </div>

      {/* Mock hero: one render tile + 3 product matches */}
      <div style={{ display: "grid", gridTemplateColumns: "220px 1fr", gap: 18, alignItems: "stretch" }}>
        <div style={{ position: "relative", borderRadius: 14, overflow: "hidden", background: "#F3ECE9", aspectRatio: "3 / 4", border: "1.5px solid rgba(28, 79, 44, 0.18)" }}>
          <img
            src="https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=520&auto=format&fit=crop&q=60"
            alt="Sample render"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
          <span style={{ position: "absolute", top: 10, left: 10, background: "#3E2A1E", color: "#FFFBC9", padding: "4px 10px", borderRadius: 999, fontSize: 10.5, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700 }}>
            Sample render
          </span>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
          {mockMatches.map((m) => (
            <div key={m.name} style={{ background: "#FFFFFF", border: "1.5px solid var(--folk-border)", borderRadius: 14, overflow: "hidden", display: "flex", flexDirection: "column" }}>
              <div style={{ position: "relative", aspectRatio: "1 / 1", background: "#F3ECE9" }}>
                <img src={m.thumb} alt={m.name} loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                <span data-testid="automatch-mock-chip" style={{ position: "absolute", top: 8, left: 8, background: "#1C4F2C", color: "#EAF3ED", fontSize: 10.5, fontWeight: 800, letterSpacing: "0.06em", padding: "3px 9px", borderRadius: 999 }}>
                  {m.match}% match
                </span>
              </div>
              <div style={{ padding: "10px 12px", display: "flex", flexDirection: "column", gap: 6, minHeight: 84 }}>
                <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 13, fontWeight: 600, color: "#2A2828", lineHeight: 1.3 }}>{m.name}</div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "auto" }}>
                  <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 12.5, color: "#68280E", fontWeight: 700 }}>{m.price}</div>
                  <div style={{ fontSize: 11, color: "var(--folk-muted)", fontStyle: "italic" }}>preview</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 12.5, lineHeight: 1.6, color: "var(--folk-muted)", fontStyle: "italic" }}>
        The full folk objects catalogue is below, browse anytime. Auto-match kicks in the moment your renders are marked final.
      </div>
    </div>
  );
}



function ObjectsMarketplaceSection({ submissionId }) {
  const [renderMatches, setRenderMatches] = useState(null);
  useEffect(() => {
    if (!submissionId) return;
    let alive = true;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/portal/${submissionId}/render-matches`);
        if (alive) setRenderMatches(res.data);
      } catch { /* silent */ }
    };
    load();
    // Poll every 15s while a render is still matching so the UI flips to live results.
    const t = setInterval(load, 15000);
    return () => { alive = false; clearInterval(t); };
  }, [submissionId]);
  const [collection, setCollection] = useState("");
  const [feed, setFeed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    axios
      .get(`${API}/objects/products`, { params: { collection: collection || undefined, limit: 12 } })
      .then((res) => { if (alive) setFeed(res.data); })
      .catch((err) => { if (alive) setError(err.response?.data?.detail || "Couldn't reach the store."); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [collection]);

  const products = feed?.products || [];
  const activeCollection = OBJECTS_COLLECTIONS.find((c) => c.key === collection) || OBJECTS_COLLECTIONS[0];
  const sourceUrl = feed?.source_url || "https://folkobjects.com/collections/all-products";

  return (
    <section className="folk-fade-in" data-testid="objects-section" style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <SectionHeader
        chapter="15"
        eyebrow="Objects marketplace"
        title="Live from folkobjects.com."
        hue="blue"
      />

      {renderMatches?.any_matched
        ? <LiveMatchResultsBlock matches={renderMatches.matches || []} />
        : <AutoMatchExplainerCard />}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 14, color: "var(--folk-muted)", maxWidth: 560, lineHeight: 1.55 }}>
          Every piece pulled fresh from the Folk Objects store. Filter by category, tap through to buy. We refresh the feed every ten minutes.
        </div>
        <a
          href={sourceUrl}
          target="_blank"
          rel="noreferrer"
          data-testid="objects-visit-store"
          className="folk-pill"
          style={{ textDecoration: "none", background: "#0F3A54", color: "#FFFBC9", border: "1.5px solid #0F3A54", padding: "9px 16px", fontSize: 12.5 }}
        >
          Explore the full store →
        </a>
      </div>

      {/* Category pills */}
      <div data-testid="objects-filters" style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {OBJECTS_COLLECTIONS.map((c) => {
          const active = c.key === collection;
          return (
            <button
              key={c.key || "all"}
              type="button"
              data-testid={`objects-filter-${c.key || "all"}`}
              onClick={() => setCollection(c.key)}
              className="folk-pill"
              style={{
                padding: "6px 14px", fontSize: 12.5,
                background: active ? "#68280E" : "transparent",
                color: active ? "#FFFBC9" : "#2A2828",
                border: `1.5px solid ${active ? "#68280E" : "var(--folk-border)"}`,
                textTransform: "lowercase",
                cursor: "pointer",
              }}
            >
              {c.label}
            </button>
          );
        })}
      </div>

      {/* Grid */}
      {loading && !feed ? (
        <div data-testid="objects-loading" style={{ background: "var(--folk-card)", border: "1.5px dashed var(--folk-border)", borderRadius: 18, padding: 28, textAlign: "center", color: "var(--folk-muted)" }}>
          fetching {activeCollection.label} from folkobjects.com…
        </div>
      ) : products.length === 0 ? (
        <div data-testid="objects-empty" style={{ background: "#FFFBC9", border: "1.5px solid rgba(104, 40, 14, 0.18)", borderRadius: 18, padding: 24, color: "#68280E", lineHeight: 1.6 }}>
          {error || feed?.hint || `we couldn't pull ${activeCollection.label} right now.`}{" "}
          <a href={sourceUrl} target="_blank" rel="noreferrer" style={{ color: "#68280E", fontWeight: 600, textDecoration: "underline" }}>
            Browse folkobjects.com →
          </a>
        </div>
      ) : (
        <div
          data-testid="objects-live-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
            gap: 16,
          }}
        >
          {products.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      )}

      {feed?.stale && (
        <div style={{ fontSize: 12, color: "var(--folk-hint)", fontFamily: "Host Grotesk, sans-serif" }}>
          showing the last cached feed — folkobjects.com couldn't be reached just now.
        </div>
      )}
    </section>
  );
}

function ProductCard({ product }) {
  const [hover, setHover] = useState(false);
  const priceLabel = formatINR(product.price);
  const compareLabel = product.compare_at && product.compare_at > product.price ? formatINR(product.compare_at) : null;
  const activeImage = hover && product.image_alt ? product.image_alt : product.image;

  return (
    <a
      href={product.url}
      target="_blank"
      rel="noreferrer"
      data-testid={`product-card-${product.handle}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: "var(--folk-card)",
        border: "1.5px solid var(--folk-border)",
        borderRadius: 14,
        padding: 12,
        display: "flex",
        flexDirection: "column",
        gap: 8,
        textDecoration: "none",
        color: "inherit",
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
        transform: hover ? "translateY(-2px)" : "translateY(0)",
        boxShadow: hover ? "0 10px 24px rgba(27, 26, 26, 0.08)" : "none",
      }}
    >
      <div style={{ position: "relative", aspectRatio: "1 / 1", borderRadius: 10, overflow: "hidden", background: "#F3ECE9" }}>
        {activeImage ? (
          <img
            src={activeImage}
            alt={product.title}
            loading="lazy"
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", transition: "opacity 0.2s ease" }}
          />
        ) : (
          <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Playfair Display', serif", fontSize: 32, color: "rgba(104, 40, 14, 0.4)" }}>
            {product.title.slice(0, 1).toLowerCase()}
          </div>
        )}
        {product.on_sale && (
          <span style={{ position: "absolute", top: 8, left: 8, background: "#68280E", color: "#FFFBC9", fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700, borderRadius: 999, padding: "3px 8px" }}>
            sale
          </span>
        )}
        {!product.available && (
          <span style={{ position: "absolute", top: 8, right: 8, background: "#1B1A1A", color: "#FFFBC9", fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700, borderRadius: 999, padding: "3px 8px" }}>
            sold out
          </span>
        )}
      </div>
      {product.product_type && (
        <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 10.5, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-muted)", fontWeight: 600 }}>
          {product.product_type}
        </div>
      )}
      <div style={{ fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: "-0.028em", fontSize: 15.5, color: "#1B1A1A", lineHeight: 1.25, textTransform: "lowercase" }}>
        {String(product.title).toLowerCase()}
      </div>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
          <span style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 14, fontWeight: 700, color: "#68280E" }}>{priceLabel}</span>
          {compareLabel && (
            <span style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 11.5, color: "var(--folk-muted)", textDecoration: "line-through" }}>{compareLabel}</span>
          )}
        </div>
        <span style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 11.5, color: "#0F3A54", fontWeight: 600 }}>
          view →
        </span>
      </div>
    </a>
  );
}


function DesignProcessSection({ override }) {
  const d = DESIGN_PROCESS;
  const eyebrow = override?.eyebrow || d.eyebrow;
  const title = override?.title || d.title;
  const intro = (override?.intro_paragraphs && override.intro_paragraphs.length)
    ? override.intro_paragraphs
    : d.intro;
  return (
    <section className="folk-fade-in">
      <SectionHeader chapter="02" eyebrow={eyebrow} title={title} hue="maroon" />
      <div style={{ maxWidth: 780, display: "flex", flexDirection: "column", gap: 10, marginBottom: 28 }}>
        {intro.map((p, i) => (
          <p key={i} className="folk-body" style={{ margin: 0 }}>{p}</p>
        ))}
      </div>
      <ol style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 18 }}>
        {d.phases.map((p) => (
          <li
            key={p.num}
            style={{
              background: "var(--folk-card)",
              border: "1.5px solid var(--folk-border)",
              borderRadius: 18,
              padding: 28,
            }}
            data-testid={`process-phase-${p.num}`}
          >
            <div style={{ display: "flex", gap: 20, alignItems: "flex-start", flexWrap: "wrap" }}>
              <div style={{ fontFamily: "Playfair Display, serif", letterSpacing: '-0.015em', fontSize: 34, color: "var(--folk-accent)", minWidth: 60 }}>{p.num}</div>
              <div style={{ flex: 1, minWidth: 240 }}>
                <div style={{ fontFamily: "Playfair Display, serif", letterSpacing: '-0.015em', fontSize: 24, marginBottom: 4 }}>{p.name}</div>
                <div style={{ color: "var(--folk-muted)", fontSize: 15, fontStyle: "italic", marginBottom: 14 }}>{p.subtitle}</div>
                <div className="folk-body" style={{ marginBottom: p.details ? 22 : 0 }}>{p.body}</div>
                {p.details && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                    {p.details.map((det, di) => (
                      <div key={di} style={{ background: "#BADDF0", borderRadius: 12, padding: 18 }}>
                        <div style={{ fontFamily: "Playfair Display, serif", letterSpacing: '-0.015em', fontSize: 17, marginBottom: 8 }}>{det.heading}</div>
                        {det.body && <p className="folk-body" style={{ margin: "0 0 10px" }}>{det.body}</p>}
                        {det.list && (
                          <ul style={{ margin: "6px 0 10px 20px", padding: 0, color: "#2A2828", fontSize: 15, lineHeight: 1.65 }}>
                            {det.list.map((li, ii) => <li key={ii}>{li}</li>)}
                          </ul>
                        )}
                        {det.note && <div style={{ color: "var(--folk-muted)", fontSize: 14, fontStyle: "italic", marginTop: 6 }}>{det.note}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

function RoadmapSection({ roadmapContent }) {
  const r = ROADMAP;
  const intro = roadmapContent?.intro || r.intro;
  const totalLine = roadmapContent?.total_line || r.totalLine;
  const timelinesNote = roadmapContent?.timelines_note || r.note;
  return (
    <section className="folk-fade-in">
      <SectionHeader chapter="03" eyebrow={r.eyebrow} title={r.title} hue="blue" />
      <p className="folk-body" style={{ maxWidth: 780, marginBottom: 24 }}>{intro}</p>

      <div style={{ background: "var(--folk-card)", border: "1.5px solid var(--folk-border)", borderRadius: 18, overflow: "hidden", marginBottom: 22 }}>
        <div style={{ display: "grid", gridTemplateColumns: "80px 1.4fr 2fr 160px", padding: "14px 20px", background: "#BADDF0", fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase", color: "#1B4A66" }}>
          <div>Phase</div>
          <div>Name</div>
          <div>What we're doing</div>
          <div>Timeline</div>
        </div>
        {r.phases.map((p) => (
          <div
            key={p.num}
            data-testid={`roadmap-row-${p.num}`}
            style={{ display: "grid", gridTemplateColumns: "80px 1.4fr 2fr 160px", padding: "18px 20px", borderTop: "1px solid var(--folk-border)", alignItems: "center" }}
          >
            <div style={{ fontFamily: "Playfair Display, serif", letterSpacing: '-0.015em', fontSize: 20, color: "var(--folk-accent)" }}>{p.num}</div>
            <div style={{ fontFamily: "Playfair Display, serif", letterSpacing: '-0.015em', fontSize: 17, color: "#2A2828" }}>{p.name}</div>
            <div style={{ color: "#2A2828", fontSize: 14.5 }}>{p.body}</div>
            <div style={{ color: "var(--folk-muted)", fontSize: 14 }}>{p.weeks}</div>
          </div>
        ))}
      </div>

      <div style={{ maxWidth: 780, color: "#2A2828", fontSize: 15, marginBottom: 24 }}>
        <strong>{totalLine}</strong>
      </div>
      <div style={{ maxWidth: 780, color: "var(--folk-muted)", fontSize: 14.5, lineHeight: 1.65, marginBottom: 36 }}>
        <div style={{ fontFamily: "Playfair Display, serif", letterSpacing: '-0.015em', color: "var(--folk-text)", fontSize: 17, marginBottom: 6 }}>A note on timelines</div>
        {timelinesNote}
      </div>

      <div>
        <div style={{ fontFamily: "Playfair Display, serif", letterSpacing: '-0.015em', fontSize: 20, marginBottom: 14 }}>The simple version</div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          {r.simple.map((s, i) => (
            <React.Fragment key={i}>
              <div
                style={{
                  padding: "10px 16px",
                  borderRadius: 999,
                  border: "1.5px solid var(--folk-border)",
                  background: "var(--folk-card)",
                  fontSize: 14,
                  color: "#2A2828",
                  textAlign: "center",
                  minWidth: 110,
                }}
              >
                {s.weeks && <div style={{ fontSize: 12, color: "var(--folk-hint)" }}>{s.weeks}</div>}
                <div style={{ fontFamily: "Playfair Display, serif", letterSpacing: '-0.015em', fontSize: 16 }}>{s.label}</div>
              </div>
              {i < r.simple.length - 1 && <div style={{ color: "var(--folk-hint)", fontSize: 18 }}>→</div>}
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}

function WorkingTogetherSection({ override }) {
  const w = WORKING_TOGETHER;
  const eyebrow = override?.eyebrow || w.eyebrow;
  const title = override?.title || w.title;
  const intro = override?.intro || w.intro;
  const outroTitle = override?.outro_title || w.outro.title;
  const outroBody = override?.outro_body || w.outro.body;
  const outroClosing = override?.outro_closing || w.outro.closing;
  return (
    <section className="folk-fade-in">
      <SectionHeader chapter="04" eyebrow={eyebrow} title={title} hue="yellow" />
      <p className="folk-body" style={{ maxWidth: 780, marginBottom: 32 }}>{intro}</p>

      <div style={{ marginBottom: 40 }}>
        <div style={{ fontFamily: "Playfair Display, serif", letterSpacing: '-0.015em', fontSize: 22, marginBottom: 16 }}>What you can expect from Folk</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 14 }}>
          {w.expectations.map((e) => (
            <NumberedCard key={e.num} num={e.num} name={e.name} body={e.body} testId={`expect-${e.num}`} />
          ))}
        </div>
      </div>

      <div style={{ marginBottom: 40 }}>
        <div style={{ fontFamily: "Playfair Display, serif", letterSpacing: '-0.015em', fontSize: 22, marginBottom: 16 }}>What we need from you</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 14 }}>
          {w.needs.map((e) => (
            <NumberedCard key={e.num} num={e.num} name={e.name} body={e.body} testId={`need-${e.num}`} />
          ))}
        </div>
      </div>

      <div style={{ background: "var(--folk-card)", border: "1.5px solid var(--folk-accent)", borderRadius: 18, padding: 28, maxWidth: 780, marginBottom: 40 }}>
        <div style={{ fontFamily: "Playfair Display, serif", letterSpacing: '-0.015em', fontSize: 22, marginBottom: 10 }}>{outroTitle}</div>
        <p className="folk-body" style={{ marginBottom: 14 }}>{outroBody}</p>
        <p style={{ fontFamily: "Playfair Display, serif", letterSpacing: '-0.015em', fontSize: 20, color: "var(--folk-accent)", margin: 0 }}>{outroClosing}</p>
      </div>

      <div>
        <div style={{ fontFamily: "Playfair Display, serif", letterSpacing: '-0.015em', fontSize: 22, marginBottom: 16 }}>Documents that come with your Folk docket</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 14 }}>
          {w.documents.map((d) => (
            <NumberedCard key={d.num} num={d.num} name={d.name} body={d.body} testId={`doc-${d.num}`} />
          ))}
        </div>
      </div>
    </section>
  );
}

function NumberedCard({ num, name, body, testId }) {
  return (
    <div
      data-testid={testId}
      style={{
        background: "var(--folk-card)",
        border: "1.5px solid var(--folk-border)",
        borderRadius: 14,
        padding: 20,
      }}
    >
      <div style={{ fontFamily: "Playfair Display, serif", letterSpacing: '-0.015em', fontSize: 22, color: "var(--folk-accent)", marginBottom: 6 }}>{num}</div>
      <div style={{ fontFamily: "Playfair Display, serif", letterSpacing: '-0.015em', fontSize: 18, marginBottom: 8 }}>{name}</div>
      <div style={{ fontSize: 14.5, color: "#2A2828", lineHeight: 1.65 }}>{body}</div>
    </div>
  );
}

function UploadPlaceholderSection({ eyebrow, chapter, title, body, note, hue = "maroon", kind, data, embedded = false, accept, dropHint, maxMB = 25 }) {
  const [items, setItems] = useState(data?.[kind] || []);
  const [busy, setBusy] = useState(false);
  const inputRef = React.useRef(null);
  const backend = process.env.REACT_APP_BACKEND_URL;
  const asAdmin = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("asAdmin") === "1";
  const uploader = asAdmin ? "admin" : "homeowner";

  useEffect(() => { setItems(data?.[kind] || []); }, [data, kind]);

  const onFiles = async (files) => {
    if (!files || !files.length || !data?.id) return;
    setBusy(true);
    for (const file of Array.from(files)) {
      const form = new FormData();
      form.append("file", file);
      form.append("uploaded_by", uploader);
      try {
        const { data: res } = await axios.post(`${backend}/api/portal/${data.id}/uploads/${kind}`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setItems((prev) => [...prev, res.file]);
      } catch (err) {
        alert(err.response?.data?.detail || "Upload failed");
      }
    }
    setBusy(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = async (fid) => {
    if (!window.confirm("Remove this file? It stays with your Folk team but disappears from the gallery.")) return;
    try {
      await axios.delete(`${backend}/api/portal/${data.id}/uploads/${kind}/${fid}`);
      setItems((prev) => prev.filter((f) => f.id !== fid));
    } catch { /* silent */ }
  };

  const defaultAccept = kind === "renders" ? "image/*" : "image/*,application/pdf";
  const acceptTypes = accept || defaultAccept;
  const defaultHint = (
    kind === "renders" ? "Drop renders / images here, or click to select"
    : kind === "layouts" ? "Drop layouts / PDFs / images here, or click to select"
    : kind === "custom_furniture" ? "Drop drawings, references or PDFs here, or click to select"
    : kind === "materials_finishes" ? "Drop material photos or PDFs here, or click to select"
    : kind === "paint_textures" ? "Drop paint chips, swatches or images here, or click to select"
    : kind === "upholstery" ? "Drop fabric swatches or images here, or click to select"
    : "Drop images or PDFs here, or click to select"
  );
  const hint = dropHint || defaultHint;

  const dropzone = (
    <>
      <div
        data-testid={`upload-dropzone-${kind}`}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); onFiles(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        style={{
          border: "1.5px dashed var(--folk-border)",
          borderRadius: 16, padding: "28px 20px", textAlign: "center",
          background: embedded ? "#FFF" : "var(--folk-card)", cursor: "pointer",
          marginBottom: 22, maxWidth: embedded ? "100%" : 820,
        }}
      >
        <div style={{ fontSize: 30, opacity: 0.35, marginBottom: 4 }}>+</div>
        <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 14, color: "var(--folk-muted)" }}>
          {busy ? "Uploading…" : hint}
        </div>
        <div style={{ fontSize: 12, color: "var(--folk-hint)", marginTop: 4 }}>Max {maxMB}MB per file · both team and homeowner can add</div>
        <input
          ref={inputRef}
          data-testid={`upload-input-${kind}`}
          type="file"
          multiple
          accept={acceptTypes}
          style={{ display: "none" }}
          onChange={(e) => onFiles(e.target.files)}
        />
      </div>

      {items.length === 0 ? (
        note ? (
          <div style={{ background: "#BADDF0", border: "1.5px solid rgba(27, 74, 102, 0.2)", borderRadius: 14, padding: 18, maxWidth: embedded ? "100%" : 820, color: "#0F3A54", fontSize: 14.5, lineHeight: 1.6 }}>
            {note}
          </div>
        ) : null
      ) : (
        <div data-testid={`upload-gallery-${kind}`} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 14, maxWidth: embedded ? "100%" : 820 }}>
          {items.map((f) => {
            const url = `${backend}/api/uploads/${f.path}`;
            const isPdf = f.content_type === "application/pdf";
            const isVideo = (f.content_type || "").startsWith("video/");
            return (
              <div key={f.id} data-testid={`upload-item-${f.id}`} style={{ background: "var(--folk-card)", border: "1.5px solid var(--folk-border)", borderRadius: 14, overflow: "hidden", position: "relative" }}>
                <div style={{ aspectRatio: "4/3", background: "#F3ECE9" }}>
                  {isPdf ? (
                    <a href={url} target="_blank" rel="noreferrer" style={{ display: "flex", height: "100%", alignItems: "center", justifyContent: "center", color: "var(--folk-accent)", fontSize: 14, fontFamily: "Host Grotesk, sans-serif", textDecoration: "none" }}>
                      Open PDF
                    </a>
                  ) : isVideo ? (
                    <video src={url} controls preload="metadata" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", background: "#000" }} />
                  ) : (
                    <a href={url} target="_blank" rel="noreferrer" style={{ display: "block", height: "100%" }}>
                      <img src={url} alt={f.filename} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                    </a>
                  )}
                </div>
                <div style={{ padding: "10px 12px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                  <div style={{ fontSize: 12.5, color: "var(--folk-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={f.filename}>{f.filename}</div>
                  <button type="button" onClick={() => remove(f.id)} data-testid={`upload-delete-${f.id}`} style={{ background: "none", border: "none", color: "var(--folk-muted)", cursor: "pointer", fontSize: 12, textDecoration: "underline" }}>Remove</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );

  if (embedded) {
    return (
      <div data-testid={`upload-embedded-${kind}`} style={{ background: "var(--folk-card)", border: "1.5px solid var(--folk-border)", borderRadius: 18, padding: "22px 24px", maxWidth: 820 }}>
        <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "#0F3A54", fontWeight: 700, marginBottom: 6 }}>{eyebrow}</div>
        {title && <div style={{ fontFamily: "Playfair Display, serif", letterSpacing: "-0.028em", fontSize: 22, textTransform: "lowercase", marginBottom: 6 }}>{title.toLowerCase()}</div>}
        {body && <p className="folk-body" style={{ marginBottom: 16, fontSize: 14.5 }}>{body}</p>}
        {dropzone}
      </div>
    );
  }

  return (
    <section className="folk-fade-in" data-testid={`upload-section-${eyebrow.toLowerCase()}`}>
      <SectionHeader chapter={chapter} eyebrow={eyebrow} title={title} hue={hue} />
      <p className="folk-body" style={{ maxWidth: 780, marginBottom: 24 }}>{body}</p>
      {dropzone}
    </section>
  );
}

function YourDesignerSection({ submissionId, data }) {
  const [designers, setDesigners] = useState(null);
  useEffect(() => {
    if (!submissionId) return;
    axios.get(`${API}/portal/${submissionId}/designers`)
      .then((res) => setDesigners(res.data?.designers || []))
      .catch(() => setDesigners([]));
  }, [submissionId]);

  return (
    <section className="folk-fade-in" data-testid="your-designer-section">
      <SectionHeader chapter="02b" eyebrow="Your designer" title="the folk hands on your home." hue="maroon" />
      {designers === null ? (
        <div style={{ color: "var(--folk-muted)" }}>Loading your team…</div>
      ) : designers.length === 0 ? (
        <DesignerAwaitingCard first={data?.contact_name?.split(" ")[0]} />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
          {designers.map((d) => (
            <DesignerProfileCard key={d.email} designer={d} />
          ))}
        </div>
      )}
    </section>
  );
}

function DesignerAwaitingCard({ first }) {
  return (
    <div
      data-testid="designer-awaiting"
      style={{
        background: "#FFFBC9",
        border: "1.5px solid rgba(104, 40, 14, 0.2)",
        borderRadius: 20,
        padding: 28,
        maxWidth: 720,
        color: "#68280E",
      }}
    >
      <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700, marginBottom: 10 }}>Matching you now</div>
      <div className="folk-serif" style={{ fontSize: 24, lineHeight: 1.2, marginBottom: 10, textTransform: "lowercase", letterSpacing: "-0.028em" }}>
        we're pairing {first ? first.toLowerCase() : "you"} with the right folk designer.
      </div>
      <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 15, lineHeight: 1.6, color: "#2A2828" }}>
        Once your report is reviewed, we'll introduce the designer whose sensibility fits your home the best.
        Their profile, portfolio, and how to reach them will show up right here.
      </div>
    </div>
  );
}

function DesignerProfileCard({ designer }) {
  const prefs = designer.contact_prefs || {};
  return (
    <div
      data-testid={`designer-card-${designer.email}`}
      style={{
        background: "var(--folk-card)",
        border: "1.5px solid var(--folk-border)",
        borderRadius: 20,
        padding: 26,
        maxWidth: 820,
      }}
    >
      <div style={{ display: "flex", gap: 22, alignItems: "flex-start", flexWrap: "wrap" }}>
        <DesignerAvatar url={designer.photo_url} name={designer.name || designer.email} size={112} />
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-accent)", fontWeight: 700 }}>Your designer</div>
          <div className="folk-serif" style={{ fontSize: 28, lineHeight: 1.2, textTransform: "lowercase", letterSpacing: "-0.028em", marginTop: 4 }}>
            {(designer.name || designer.email).toLowerCase()}
          </div>
          {designer.tagline && (
            <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontStyle: "italic", fontSize: 17, color: "#5A3E2E", marginTop: 6 }}>
              "{designer.tagline}"
            </div>
          )}
          {typeof designer.years_experience === "number" && (
            <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 13, color: "var(--folk-muted)", marginTop: 8 }}>
              {designer.years_experience} year{designer.years_experience === 1 ? "" : "s"} designing homes
            </div>
          )}
        </div>
      </div>

      {designer.bio && (
        <p style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 15, lineHeight: 1.65, color: "#2A2828", margin: "22px 0 0" }}>
          {designer.bio}
        </p>
      )}

      {designer.specialties && designer.specialties.length > 0 && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 18 }}>
          {designer.specialties.map((s) => (
            <span
              key={s}
              style={{
                fontSize: 12,
                padding: "5px 12px",
                borderRadius: 999,
                background: "#BADDF0",
                color: "#0F3A54",
                fontFamily: "Host Grotesk, sans-serif",
                fontWeight: 500,
              }}
            >
              {s}
            </span>
          ))}
        </div>
      )}

      {designer.portfolio && designer.portfolio.length > 0 && (
        <div style={{ marginTop: 22 }}>
          <div style={{ fontSize: 11.5, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-hint)", fontWeight: 700, marginBottom: 10 }}>Work we love</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 }}>
            {designer.portfolio.map((p) => (
              <figure key={p.id || p.url} style={{ margin: 0 }}>
                <img
                  src={p.url}
                  alt={p.caption || ""}
                  loading="lazy"
                  style={{ width: "100%", height: 180, objectFit: "cover", borderRadius: 12, background: "#EFE5E1" }}
                />
                {p.caption && (
                  <figcaption style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 12.5, color: "var(--folk-muted)", marginTop: 6 }}>
                    {p.caption}
                  </figcaption>
                )}
              </figure>
            ))}
          </div>
        </div>
      )}

      {(prefs.whatsapp || prefs.email_public || prefs.call) && (
        <div style={{ marginTop: 22, display: "flex", flexWrap: "wrap", gap: 10 }}>
          {prefs.whatsapp && (
            <a
              href={`https://wa.me/${prefs.whatsapp.replace(/[^0-9+]/g, "")}`}
              target="_blank"
              rel="noreferrer"
              data-testid={`designer-whatsapp-${designer.email}`}
              className="folk-pill"
              style={{ fontSize: 13, textDecoration: "none", background: "#1F7A4C", color: "#FFFBF3", border: "1.5px solid #1F7A4C" }}
            >
              WhatsApp
            </a>
          )}
          {prefs.email_public && (
            <a
              href={`mailto:${prefs.email_public}`}
              data-testid={`designer-email-${designer.email}`}
              className="folk-pill"
              style={{ fontSize: 13, textDecoration: "none", background: "transparent", color: "var(--folk-accent)", border: "1.5px solid var(--folk-accent)" }}
            >
              Email
            </a>
          )}
          {prefs.call && (
            <a
              href={`tel:${prefs.call.replace(/\s+/g, "")}`}
              data-testid={`designer-call-${designer.email}`}
              className="folk-pill"
              style={{ fontSize: 13, textDecoration: "none", background: "transparent", color: "var(--folk-accent)", border: "1.5px solid var(--folk-accent)" }}
            >
              Call
            </a>
          )}
        </div>
      )}
    </div>
  );
}

function DesignerAvatar({ url, name, size = 96 }) {
  const initial = (name || "?").trim().slice(0, 1).toUpperCase();
  const s = { width: size, height: size, borderRadius: "50%", flexShrink: 0 };
  if (url) return <img src={url} alt={name || ""} style={{ ...s, objectFit: "cover", background: "#EFE5E1" }} />;
  return (
    <div style={{ ...s, display: "flex", alignItems: "center", justifyContent: "center", background: "#FFFBC9", color: "#68280E", fontFamily: "Playfair Display, serif", fontSize: size * 0.42, fontWeight: 600 }}>
      {initial}
    </div>
  );
}

function DesignerHandoffCard({ submissionId, onOpen }) {
  const [designers, setDesigners] = useState([]);
  useEffect(() => {
    if (!submissionId) return;
    axios.get(`${API}/portal/${submissionId}/designers`)
      .then((res) => setDesigners(res.data?.designers || []))
      .catch(() => setDesigners([]));
  }, [submissionId]);
  if (!designers.length) return null;
  const first = designers[0];
  return (
    <div
      data-testid="report-designer-handoff"
      onClick={onOpen}
      style={{
        background: "#0F3A54",
        color: "#FFFBF3",
        borderRadius: 20,
        padding: 22,
        maxWidth: 820,
        cursor: "pointer",
        display: "flex",
        gap: 18,
        alignItems: "center",
      }}
    >
      <DesignerAvatar url={first.photo_url} name={first.name || first.email} size={72} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11.5, letterSpacing: "0.18em", textTransform: "uppercase", color: "#BADDF0", fontWeight: 700 }}>Meet your designer</div>
        <div className="folk-serif" style={{ fontSize: 22, lineHeight: 1.2, marginTop: 4, textTransform: "lowercase", letterSpacing: "-0.028em" }}>
          {(first.name || first.email).toLowerCase()}{designers.length > 1 ? ` +${designers.length - 1} more` : ""}
        </div>
        {first.tagline && (
          <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 13.5, color: "#D6E5EE", marginTop: 4 }}>
            {first.tagline}
          </div>
        )}
      </div>
      <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 13, color: "#BADDF0", flexShrink: 0 }}>Open profile →</div>
    </div>
  );
}



function ProjectMoodboardSection({ data }) {
  const pinUrl = data?.pinterest_url;
  return (
    <section className="folk-fade-in" data-testid="project-moodboard-section">
      <SectionHeader chapter="06" eyebrow="Project moodboard" title="Your shared visual language." hue="blue" />
      <div
        data-testid="moodboard-note"
        style={{
          background: "#BADDF0",
          border: "1.5px solid rgba(27, 74, 102, 0.2)",
          borderRadius: 14,
          padding: "18px 20px",
          maxWidth: 780,
          marginBottom: 24,
          color: "#0F3A54",
          fontSize: 15,
          lineHeight: 1.65,
        }}
      >
        Add inspiration pins from anywhere, react to what your team drops in, and star the ones that must make it into your home. Your Folk team can see this in real time.
      </div>

      {/* Shared Pinterest board card — link if the team has set one, coming-soon otherwise */}
      <div
        data-testid="pinterest-card"
        style={{
          background: pinUrl ? "#FFFBC9" : "var(--folk-card)",
          border: `1.5px solid ${pinUrl ? "rgba(104, 40, 14, 0.2)" : "var(--folk-border)"}`,
          borderRadius: 18,
          padding: "22px 24px",
          maxWidth: 780,
          marginBottom: 28,
          display: "flex",
          gap: 18,
          alignItems: "flex-start",
          flexWrap: "wrap",
        }}
      >
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "#68280E", fontWeight: 700, marginBottom: 6, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            Shared Pinterest board
            {!pinUrl && (
              <span data-testid="pinterest-coming-soon" style={{ background: "#0F3A54", color: "#FFFBC9", fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700, borderRadius: 999, padding: "2px 8px" }}>coming soon</span>
            )}
          </div>
          <div style={{ fontFamily: "Playfair Display, serif", letterSpacing: "-0.028em", fontSize: 22, lineHeight: 1.2, textTransform: "lowercase", marginBottom: 8 }}>
            {pinUrl ? "our team's curated Pinterest board for you." : "your team is putting together a Pinterest board."}
          </div>
          <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 14.5, lineHeight: 1.6, color: "#2A2828" }}>
            {pinUrl
              ? "Every pin our team has saved for your home lives here. Tap through to see it live — new pins land as we curate."
              : "Once your Folk team publishes it, this space will hold every pin they've saved for your home. Soon it'll auto-sync into the moodboard below so pinning happens without leaving the portal."}
          </div>
        </div>
        {pinUrl ? (
          <a
            href={pinUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="pinterest-open-btn"
            className="folk-pill"
            style={{ background: "#E60023", color: "#FFF", border: "1.5px solid #C50022", textDecoration: "none", padding: "12px 22px", fontSize: 14, fontWeight: 600, whiteSpace: "nowrap" }}
          >
            Open on Pinterest ↗
          </a>
        ) : (
          <span
            data-testid="pinterest-await"
            style={{
              fontFamily: "Host Grotesk, sans-serif",
              fontSize: 12,
              color: "var(--folk-muted)",
              lineHeight: 1.5,
              maxWidth: 200,
              textAlign: "right",
            }}
          >
            We'll drop the link here as soon as it's ready.
          </span>
        )}
      </div>

      {pinUrl && <PinterestEmbed url={pinUrl} />}
      <FolkMoodboard submissionId={data?.id} />
    </section>
  );
}


function StyleReferencesSection({ data }) {
  const answers = data?.answers || {};
  const styleCsv = answers["Preferred styles"] || "";
  const selectedStyleValues = styleCsv
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  const selectedStyles = STYLE_DEFS.filter((d) => selectedStyleValues.includes(d.value));

  const PREF_LABEL_TO_KEY = {
    "Furniture feel": "furniture",
    "Solids or patterns": "pattern",
    "Florals or geometrics": "motif",
    "Rug preference": "rug",
    "Lighting mood": "lighting",
    "Walls": "walls",
  };
  const selectedPrefs = PREF_DEFS.map((d) => {
    const label = Object.keys(PREF_LABEL_TO_KEY).find((k) => PREF_LABEL_TO_KEY[k] === d.key);
    const pick = label ? answers[label] : null;
    if (pick !== "a" && pick !== "b") return null;
    return {
      key: d.key,
      question: d.question,
      chosenLabel: pick === "a" ? d.aLabel : d.bLabel,
      img: pick === "a" ? d.aImg : d.bImg,
    };
  }).filter(Boolean);

  const anySelections = selectedStyles.length > 0 || selectedPrefs.length > 0;

  return (
    <section className="folk-fade-in" data-testid="style-refs-section">
      <SectionHeader chapter="06b" eyebrow="Design library" title="The Folk styles closest to yours." hue="blue" />
      <div style={{ maxWidth: 780, color: "var(--folk-muted)", fontSize: 15, lineHeight: 1.65, marginBottom: 28 }}>
        Pulled from what you told us in the survey — the aesthetics you're drawn to and the either/or picks you made. Think of it as the starting palette we design from.
      </div>
      {!anySelections && (
        <div style={{ color: "var(--folk-muted)", maxWidth: 640 }}>
          You didn't pick any style photos this time. That's okay. We'll cover this in our first
          working session and start your moodboard from scratch together.
        </div>
      )}

      {selectedStyles.length > 0 && (
        <div style={{ marginBottom: 36 }} data-testid="moodboard-styles">
          <div style={{ fontFamily: "Playfair Display, serif", letterSpacing: '-0.015em', fontSize: 22, marginBottom: 14 }}>
            Styles you're drawn to
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
            {selectedStyles.map((s) => (
              <div key={s.value} data-testid={`moodboard-style-${s.value}`}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
                  <div style={{ fontFamily: "Playfair Display, serif", letterSpacing: '-0.015em', fontSize: 18, color: "var(--folk-accent)" }}>
                    {s.label}
                  </div>
                  <div style={{ color: "var(--folk-muted)", fontSize: 14, fontStyle: "italic" }}>{s.desc}</div>
                </div>
                {s.hasRefs ? (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                      gap: 10,
                    }}
                  >
                    {[1, 2, 3, 4].map((n) => (
                      <div
                        key={n}
                        style={{
                          aspectRatio: "1/1",
                          borderRadius: 12,
                          overflow: "hidden",
                          background: "#F3ECE9",
                        }}
                      >
                        <img
                          src={`/refs/style-${s.slot}-${n}.png`}
                          alt={`${s.label} reference ${n}`}
                          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    style={{
                      background: "#F3ECE9",
                      border: "1.5px dashed var(--folk-border)",
                      borderRadius: 12,
                      padding: 16,
                      color: "var(--folk-muted)",
                      fontSize: 14,
                    }}
                  >
                    Reference photos for "{s.label}" will be curated by your design team.
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {selectedPrefs.length > 0 && (
        <div data-testid="moodboard-prefs">
          <div style={{ fontFamily: "Playfair Display, serif", letterSpacing: '-0.015em', fontSize: 22, marginBottom: 14 }}>
            Your look-and-feel picks
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
              gap: 14,
            }}
          >
            {selectedPrefs.map((p) => (
              <div
                key={p.key}
                data-testid={`moodboard-pref-${p.key}`}
                style={{
                  background: "var(--folk-card)",
                  border: "1.5px solid var(--folk-border)",
                  borderRadius: 14,
                  overflow: "hidden",
                }}
              >
                <div style={{ aspectRatio: "4/3", background: "#F3ECE9" }}>
                  <img
                    src={p.img}
                    alt={p.chosenLabel}
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  />
                </div>
                <div style={{ padding: "14px 16px" }}>
                  <div style={{ fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--folk-hint)", marginBottom: 4 }}>
                    {p.question}
                  </div>
                  <div style={{ fontFamily: "Playfair Display, serif", letterSpacing: '-0.015em', fontSize: 16, color: "#2A2828" }}>
                    {p.chosenLabel}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function TermsSection({ terms }) {
  const intro = terms?.intro || "The following terms govern the design services provided by Folk. These will be finalised and countersigned alongside your commercial documents before we begin the project.";
  const sections = terms?.sections?.length
    ? terms.sections
    : [
        { heading: "1. Scope of Services", body: "Folk will provide the design, procurement, project management, styling and delivery services outlined in your signed commercial documents. Any additional scope must be agreed in writing before work commences." },
        { heading: "2. Payments & Milestones", body: "Your project is structured around three payment milestones: commencement, production kick-off, and delivery. The exact schedule and amounts are set out in your invoice. Payments are non-refundable once the corresponding stage has begun." },
        { heading: "3. Production, Delivery & Timelines", body: "Indicative timelines are shared in your roadmap and are dependent on manufacturing, vendor schedules, logistics and site readiness. Folk will keep you updated on any variance and work with you towards a confirmed installation date." },
        { heading: "4. Made-to-Order Items", body: "Custom pieces produced for your project are non-returnable. Where possible, we will share samples and confirm materials before production begins." },
        { heading: "5. On-Site Requirements", body: "You (or your appointed representative) are responsible for ensuring the site is safe, accessible and ready for installation on the agreed date. Folk cannot be held responsible for delays caused by incomplete civil, electrical or plumbing works." },
        { heading: "6. Aftercare & Support", body: "Folk provides 30 days of post-installation support for products and installation-related queries. Manufacturer warranties, where applicable, are passed through to you." },
        { heading: "7. Confidentiality & Real Homes", body: "Both parties agree to keep project details confidential. With your written permission, Folk may share your project as part of the Real Homes series, never without prior consent." },
      ];
  const closing = terms?.closing || "The complete Terms & Conditions Agreement will be shared as a signable document alongside your commercial invoice. Please treat this section as an in-portal summary rather than the binding agreement.";
  return (
    <section className="folk-fade-in" data-testid="terms-section">
      <SectionHeader chapter="05" eyebrow="Terms & Conditions" title="The formal bits, kept human." hue="maroon" />
      <p className="folk-body" style={{ maxWidth: 780, marginBottom: 24 }}>{intro}</p>
      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 24 }}>
        {sections.map((s, i) => (
          <div
            key={i}
            data-testid={`terms-${i}`}
            style={{
              background: "var(--folk-card)",
              border: "1.5px solid var(--folk-border)",
              borderRadius: 14,
              padding: 20,
            }}
          >
            <div style={{ fontFamily: "Playfair Display, serif", letterSpacing: '-0.015em', fontSize: 18, marginBottom: 6 }}>{s.heading}</div>
            <div style={{ fontSize: 14.5, color: "#2A2828", lineHeight: 1.65, whiteSpace: "pre-wrap" }}>{s.body}</div>
          </div>
        ))}
      </div>
      <div style={{ background: "#BADDF0", border: "1.5px solid rgba(27, 74, 102, 0.2)", borderRadius: 14, padding: 18, maxWidth: 780, color: "#0F3A54", fontSize: 14, fontStyle: "italic" }}>
        {closing}
      </div>
    </section>
  );
}

function PortalSessionBadge() {
  const auth = useHomeownerAuth();
  const session = auth?.session;
  if (session === null) return null;
  if (session && session.email) {
    return (
      <div data-testid="portal-session-badge" style={{ display: "flex", alignItems: "center", gap: 10, background: "var(--folk-card)", border: "1.5px solid var(--folk-border)", borderRadius: 999, padding: "8px 14px" }}>
        <div style={{ width: 8, height: 8, borderRadius: 999, background: "var(--folk-accent-2)" }} />
        <div style={{ fontSize: 13, color: "var(--folk-muted)", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{session.email}</div>
        <button
          type="button"
          data-testid="portal-signout-btn"
          onClick={() => auth.logout()}
          style={{ background: "none", border: "none", cursor: "pointer", color: "var(--folk-accent)", fontFamily: "Host Grotesk, sans-serif", fontSize: 13, textDecoration: "underline" }}
        >
          Sign out
        </button>
      </div>
    );
  }
  return (
    <Link
      to="/portal-login"
      data-testid="portal-signin-link"
      style={{ display: "inline-block", padding: "10px 18px", borderRadius: 999, border: "1.5px solid rgba(27, 74, 102, 0.25)", background: "#BADDF0", color: "#0F3A54", textDecoration: "none", fontFamily: "Host Grotesk, sans-serif", fontSize: 14, fontWeight: 500 }}
    >
      Sign in
    </Link>
  );
}

const PROJECT_PHASES = [
  { key: "discovery",  label: "Discovery" },
  { key: "concept",    label: "Concept" },
  { key: "design_dev", label: "Design Development" },
  { key: "sourcing",   label: "Sourcing" },
  { key: "install",    label: "Install" },
  { key: "handover",   label: "Handover" },
];

function ProjectTimelineBar({ data, onGoToStatus }) {
  const advancePaid = !!data?.advance_paid;
  const currentKey = data?.current_phase || "discovery";
  const currentIdx = Math.max(0, PROJECT_PHASES.findIndex((p) => p.key === currentKey));
  const currentLabel = PROJECT_PHASES[currentIdx]?.label || "Discovery";
  const pct = ((currentIdx + 1) / PROJECT_PHASES.length) * 100;

  // Locked state — project hasn't been kicked off yet.
  if (!advancePaid) {
    return (
      <div
        data-testid="project-timeline-locked"
        style={{
          background: "#F1EDE8",
          border: "1.5px dashed #C9BDB1",
          borderRadius: 16,
          padding: "16px 20px",
          marginBottom: 28,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, flex: "1 1 300px" }}>
          <span aria-hidden style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            width: 34, height: 34, borderRadius: 999, background: "#68280E", color: "#FFFBC9", fontSize: 15,
          }}>
            <Lock size={16} strokeWidth={2.2} />
          </span>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <div style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: "#8A6B54", fontWeight: 700 }}>Project timeline</div>
            <div style={{ fontFamily: "Playfair Display, serif", letterSpacing: "-0.028em", fontSize: 18, color: "#3E2A1E", textTransform: "lowercase" }}>
              unlocks after your advance is paid.
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", opacity: 0.55 }}>
          {PROJECT_PHASES.map((p) => (
            <span key={p.key} style={{
              fontSize: 10.5, letterSpacing: "0.08em", textTransform: "uppercase",
              padding: "5px 10px", borderRadius: 999, border: "1px solid #C9BDB1",
              color: "#68280E", fontFamily: "Host Grotesk, sans-serif", fontWeight: 600,
            }}>
              {p.label}
            </span>
          ))}
        </div>
      </div>
    );
  }

  // Unlocked — live phase view.
  return (
    <button
      type="button"
      data-testid="project-timeline-live"
      onClick={onGoToStatus}
      style={{
        width: "100%",
        background: "#F5F8FB",
        border: "1.5px solid rgba(15, 58, 84, 0.18)",
        borderRadius: 16,
        padding: "16px 20px",
        marginBottom: 28,
        display: "flex",
        flexDirection: "column",
        gap: 12,
        cursor: "pointer",
        textAlign: "left",
        fontFamily: "inherit",
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: "#0F3A54", fontWeight: 700 }}>Project timeline</span>
          <span data-testid="project-timeline-current" style={{ fontFamily: "Playfair Display, serif", letterSpacing: "-0.028em", fontSize: 20, color: "#0F3A54", textTransform: "lowercase" }}>
            {currentLabel.toLowerCase()}
          </span>
        </div>
        <span style={{ fontSize: 12, color: "#4A6478", fontFamily: "Host Grotesk, sans-serif" }}>
          Phase {currentIdx + 1} of {PROJECT_PHASES.length} · <span style={{ textDecoration: "underline" }}>view details</span>
        </span>
      </div>

      <div style={{ position: "relative", height: 5, background: "#DCE7F0", borderRadius: 6, overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, width: `${pct}%`, background: "#0F3A54", borderRadius: 6 }} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: `repeat(${PROJECT_PHASES.length}, 1fr)`, gap: 6 }}>
        {PROJECT_PHASES.map((p, i) => {
          const done = i <= currentIdx;
          return (
            <div key={p.key} style={{
              fontSize: 10.5, letterSpacing: "0.08em", textTransform: "uppercase",
              textAlign: "center", color: done ? "#0F3A54" : "#8FA5B4",
              fontWeight: done ? 700 : 500, fontFamily: "Host Grotesk, sans-serif",
            }}>
              {p.label}
            </div>
          );
        })}
      </div>
    </button>
  );
}


function StatusSection({ data }) {
  const phases = [
    { key: "discovery", label: "Discovery" },
    { key: "concept", label: "Concept" },
    { key: "design_dev", label: "Design Development" },
    { key: "sourcing", label: "Sourcing" },
    { key: "install", label: "Install" },
    { key: "handover", label: "Handover" },
  ];
  const currentIdx = Math.max(0, phases.findIndex((p) => p.key === (data?.current_phase || "discovery")));
  const currentLabel = phases[currentIdx]?.label || "Discovery";
  const pct = ((currentIdx + 1) / phases.length) * 100;
  const updates = Array.isArray(data?.progress_updates) ? [...data.progress_updates].reverse() : [];

  return (
    <section className="folk-fade-in" data-testid="status-section">
      <SectionHeader chapter="03" eyebrow="Project status" title="Where your project is, right now." hue="blue" />

      <div style={{ background: "var(--folk-card)", border: "1.5px solid var(--folk-border)", borderRadius: 16, padding: 24, marginBottom: 32, maxWidth: 820 }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-hint)", fontWeight: 600 }}>Current phase</div>
            <div data-testid="status-current-phase" style={{ fontFamily: "Playfair Display, serif", letterSpacing: '-0.015em', fontSize: 26, color: "var(--folk-accent)" }}>{currentLabel}</div>
          </div>
          <div style={{ fontSize: 13, color: "var(--folk-muted)" }}>Phase {currentIdx + 1} of {phases.length}</div>
        </div>

        <div style={{ position: "relative", height: 6, background: "#BADDF0", borderRadius: 6, overflow: "hidden", marginBottom: 14 }}>
          <div data-testid="status-progress-bar" style={{ position: "absolute", inset: 0, width: `${pct}%`, background: "var(--folk-accent)", borderRadius: 6 }} />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: `repeat(${phases.length}, 1fr)`, gap: 8 }}>
          {phases.map((p, i) => {
            const done = i <= currentIdx;
            return (
              <div key={p.key} data-testid={`status-phase-${p.key}`} style={{ textAlign: "center", fontSize: 11.5, lineHeight: 1.35, color: done ? "var(--folk-accent)" : "var(--folk-hint)", fontWeight: done ? 600 : 400 }}>
                {p.label}
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <div style={{ fontFamily: "Playfair Display, serif", letterSpacing: '-0.015em', fontSize: 22, marginBottom: 12 }}>Recent updates from your design team</div>
        {updates.length === 0 && (
          <div data-testid="status-empty" style={{ color: "var(--folk-muted)", maxWidth: 640, padding: "18px 20px", background: "#FFFBC9", borderRadius: 12 }}>
            No updates yet. As soon as your team posts progress, it'll appear here. This page refreshes every 30 seconds.
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: 820 }}>
          {updates.map((u) => (
            <div key={u.id} data-testid={`status-update-${u.id}`} style={{ background: "var(--folk-card)", border: "1.5px solid var(--folk-border)", borderRadius: 14, padding: "16px 18px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
                <div style={{ fontSize: 12, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--folk-accent)", fontWeight: 600 }}>
                  {u.phase ? (phases.find((p) => p.key === u.phase)?.label || u.phase) : "Note"}
                </div>
                <div style={{ fontSize: 12, color: "var(--folk-hint)" }}>{formatUpdateDate(u.created_at)}</div>
              </div>
              <div style={{ fontSize: 15, lineHeight: 1.6, color: "#2A2828", marginTop: 8, whiteSpace: "pre-wrap" }}>{u.note}</div>
              <ReactionRow submissionId={data?.id} updateId={u.id} initial={u.reactions || {}} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function formatUpdateDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

function PinterestEmbed({ url }) {
  useEffect(() => {
    if (document.getElementById("pinit-loader")) {
      // Force Pinterest widget rescan when this component mounts
      if (window.PinUtils && typeof window.PinUtils.build === "function") {
        try { window.PinUtils.build(); } catch { /* noop */ }
      }
      return;
    }
    const s = document.createElement("script");
    s.id = "pinit-loader";
    s.async = true;
    s.defer = true;
    s.src = "https://assets.pinterest.com/js/pinit.js";
    document.body.appendChild(s);
  }, [url]);

  return (
    <div data-testid="pinterest-embed" style={{ background: "var(--folk-card)", border: "1.5px solid var(--folk-border)", borderRadius: 16, padding: 20, maxWidth: 900, marginBottom: 36 }}>
      <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-accent)", fontWeight: 600, marginBottom: 10 }}>Your shared Pinterest board</div>
      <a
        data-pin-do="embedBoard"
        data-pin-lang="en"
        data-pin-board-width="800"
        data-pin-scale-height="400"
        data-pin-scale-width="80"
        href={url}
      >{url}</a>
      <div style={{ marginTop: 12, fontSize: 13.5, color: "var(--folk-muted)" }}>
        <a href={url} target="_blank" rel="noreferrer" style={{ color: "var(--folk-accent)", textDecoration: "underline" }}>Open the board on Pinterest ↗</a>
      </div>
    </div>
  );
}


function PortalLoading() {
  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--folk-bg)", color: "var(--folk-muted)" }}>
      Opening your portal…
    </div>
  );
}

function PortalError({ message }) {
  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, background: "var(--folk-bg)", color: "var(--folk-text)", padding: 24, textAlign: "center" }}>
      <Logo variant="stacked" size={48} style={{ marginBottom: 8 }} />
      <h1 className="folk-h1" style={{ maxWidth: 520 }}>{message}</h1>
      <p className="folk-body" style={{ maxWidth: 440 }}>
        Please check the link, or ask your Folk Objects contact to resend it.
      </p>
      <Link to="/" style={{ color: "var(--folk-accent)", marginTop: 12 }}>Back to Folk Objects</Link>
    </div>
  );
}
